import React, { useEffect, useMemo, useRef, useState } from "react";
import "../StockInward.css";
import "./Dashboard.css";
import {
  addInwardPayment,
  addSalesPayment,
  deleteInwardPayment,
  deleteSalesPayment,
  getInwardEntries,
  getItems,
  getSalesEntries,
} from "../storage";
import PortalDropdown from "../components/PortalDropdown";
import { formatDisplayDate } from "../utils/dateFormat";
import {
  getSettlementSummary,
  getSettlementTypeLabel,
  isPaymentSettlement,
  normalizeSettlementType,
  SETTLEMENT_TYPE_PAYMENT,
} from "../utils/settlement";

const SALES_BREAKDOWN_PAGE_SIZE = 10;
const SALES_PAYMENT_DETAILS_PAGE_SIZE = 10;
const SETTLEMENT_HISTORY_PAGE_SIZE = 10;
const OVERALL_BALANCE_PAGE_SIZE = 10;
const STALL_RETAIL_DELIVERY_GROUPS = ["FISH", "SQUID", "RIVER FISH", "PRAWNS", "CRABS"];
const STALL_RETAIL_DELIVERY_GROUP_SET = new Set(STALL_RETAIL_DELIVERY_GROUPS);
const DELIVERY_BILLING_CATEGORY_DEFINITIONS = [
  {
    key: "fish-category",
    label: "FISH CATEGORY",
    groups: ["FISH", "SQUID", "RIVER FISH", "PRAWNS", "CRABS"],
  },
  {
    key: "chicken-category",
    label: "CHICKEN CATEGORY",
    groups: ["CHICKEN", "COUNTRY CHICKEN"],
  },
  {
    key: "egg-category",
    label: "EGG CATEGORY",
    groups: ["EGG"],
  },
  {
    key: "quail-category",
    label: "QUAIL CATEGORY",
    groups: ["QUAIL"],
  },
  {
    key: "mutton-category",
    label: "MUTTON CATEGORY",
    groups: ["MUTTON", "MUTTON PARTS"],
  },
  {
    key: "general-category",
    label: "GENERAL CATEGORY",
    groups: ["GENERAL"],
  },
];
const DELIVERY_BILLING_GENERAL_CATEGORY =
  DELIVERY_BILLING_CATEGORY_DEFINITIONS[DELIVERY_BILLING_CATEGORY_DEFINITIONS.length - 1];
const DELIVERY_BILLING_CATEGORY_MAP = DELIVERY_BILLING_CATEGORY_DEFINITIONS.reduce((acc, category) => {
  category.groups.forEach((group) => {
    acc[normalizeItemGroupKey(group)] = category;
  });
  return acc;
}, {});
const SALES_PULSE_BUCKETS = [
  {
    key: "delivery-billing",
    label: "Delivery Billing",
    segment: "Delivery",
    paymentType: "Sales",
    accent: "#fb7185",
    soft: "rgba(251, 113, 133, 0.16)",
  },
  {
    key: "stall-credit",
    label: "Stall Credit Sales",
    segment: "Stall",
    paymentType: "Credit",
    accent: "#22d3ee",
    soft: "rgba(34, 211, 238, 0.16)",
  },
  {
    key: "stall-retail",
    label: "Stall Retail Sales",
    segment: "Stall",
    paymentType: "Retail",
    accent: "#34d399",
    soft: "rgba(52, 211, 153, 0.16)",
  },
  {
    key: "stall-evening",
    label: "Stall Evening Sales",
    segment: "Stall",
    paymentType: "Evening",
    accent: "#f59e0b",
    soft: "rgba(245, 158, 11, 0.18)",
  },
  {
    key: "market-credit",
    label: "Market Credit Sales",
    segment: "Market",
    paymentType: "Credit",
    accent: "#60a5fa",
    soft: "rgba(96, 165, 250, 0.16)",
  },
  {
    key: "market-retail",
    label: "Market Retail Sales",
    segment: "Market",
    paymentType: "Retail",
    accent: "#f97316",
    soft: "rgba(249, 115, 22, 0.16)",
  },
];
const SALES_PULSE_BUCKET_MAP = SALES_PULSE_BUCKETS.reduce((acc, bucket) => {
  acc[bucket.key] = bucket;
  return acc;
}, {});
const SUPPLIER_WEEK_DAYS = [
  { key: "monday", label: "Monday", offset: 0 },
  { key: "tuesday", label: "Tuesday", offset: 1 },
  { key: "wednesday", label: "Wednesday", offset: 2 },
  { key: "thursday", label: "Thursday", offset: 3 },
  { key: "friday", label: "Friday", offset: 4 },
  { key: "saturday", label: "Saturday", offset: 5 },
  { key: "sunday", label: "Sunday", offset: 6 },
];
const OVERALL_FILTER_VALUE = "__overall__";
const DASHBOARD_PAYMENT_TYPE_OPTIONS = ["Any", "UPI", "Cash", "Credit", "Cheque", "Acc Transfer", "Razorpay", "Card"];
const DASHBOARD_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatMoney(value) {
  const num = Number(value) || 0;
  return num.toFixed(2);
}

function toLocalYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeYmd(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return toLocalYmd(date);
}

function normalizeYm(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}$/.test(raw)) return raw;
  const asDate = normalizeYmd(raw);
  if (asDate) return asDate.slice(0, 7);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function addDaysToYmd(ymd, days) {
  const base = normalizeYmd(ymd);
  if (!base) return "";
  const date = new Date(`${base}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + Number(days || 0));
  return toLocalYmd(date);
}

function startOfWeekMonday(ymd) {
  const base = normalizeYmd(ymd);
  if (!base) return "";
  const date = new Date(`${base}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const weekday = date.getDay();
  const diff = weekday === 0 ? -6 : 1 - weekday;
  date.setDate(date.getDate() + diff);
  return toLocalYmd(date);
}

function startOfMonth(ym) {
  const monthKey = normalizeYm(ym);
  return monthKey ? `${monthKey}-01` : "";
}

function endOfMonth(ym) {
  const monthKey = normalizeYm(ym);
  if (!monthKey) return "";
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year, month, 0);
  return toLocalYmd(date);
}

function formatMonthLabel(ym) {
  const monthKey = normalizeYm(ym);
  if (!monthKey) return "";
  const date = new Date(`${monthKey}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return monthKey;
  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(date);
}

function makeSupplierPaymentForm() {
  return {
    date: toLocalYmd(new Date()),
    amount: "",
    type: SETTLEMENT_TYPE_PAYMENT,
    mode: "Cash",
  };
}

function normalizeDashboardPaymentMode(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "any") return "Any";
  if (raw === "upi") return "UPI";
  if (raw === "cash") return "Cash";
  if (raw === "cheque" || raw === "check") return "Cheque";
  if (raw === "acc transfer" || raw === "account transfer" || raw === "bank transfer") return "Acc Transfer";
  if (raw === "razorpay") return "Razorpay";
  if (raw === "card" || raw === "credit card" || raw === "debit card") return "Card";
  if (raw === "credit") return "Credit";
  return "Cash";
}

function makeOverallBreakdownState() {
  return {
    label: "",
    rows: [],
  };
}

function makeOverallBalanceModalState() {
  return {
    open: false,
    title: "",
    entityLabel: "",
    amountLabel: "",
    rows: [],
  };
}

function makeOverallBalanceVisibleColumns() {
  return {
    entries: true,
    totalAmount: true,
    paidAmount: true,
    knockOffAmount: true,
    balanceAmount: true,
  };
}

function buildMonthWeekOptions(monthValue) {
  const monthKey = normalizeYm(monthValue);
  const monthStart = startOfMonth(monthKey);
  const monthEnd = endOfMonth(monthKey);
  if (!monthKey || !monthStart || !monthEnd) return [];

  const startWeek = startOfWeekMonday(monthStart);
  const endWeek = startOfWeekMonday(monthEnd);
  const result = [];
  let index = 1;

  for (let cursor = startWeek; cursor && cursor <= endWeek; cursor = addDaysToYmd(cursor, 7)) {
    const weekStart = cursor < monthStart ? monthStart : cursor;
    const rawWeekEnd = addDaysToYmd(cursor, 6);
    const weekEnd = rawWeekEnd > monthEnd ? monthEnd : rawWeekEnd;
    result.push({
      key: cursor,
      index,
      start: weekStart,
      end: weekEnd,
      label: `Week ${index} ${formatDisplayDate(weekStart)} to ${formatDisplayDate(weekEnd)}`,
    });
    index += 1;
  }

  return result;
}

function dashboardCustomerLabel(customerText) {
  const raw = String(customerText || "").trim();
  if (!raw) return "Walk-in";
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return "Walk-in";
  const name = lines[0] || "";
  const phone = lines[1] || "";
  return [name, phone].filter(Boolean).join(" - ") || "Walk-in";
}

function isOverallFilter(value) {
  return String(value || "").trim() === OVERALL_FILTER_VALUE;
}

function displayDashboardFilterValue(value) {
  return isOverallFilter(value) ? "Overall" : String(value || "").trim();
}

function isEveningSalesSource(value) {
  return String(value || "").trim().toLowerCase() === "evening sales";
}

function isMaterialPurchaseEntry(entry) {
  return String(entry?.category || "General").trim() === "General";
}

function normalizeSearchText(value) {
  return String(value || "").trim().toLowerCase();
}

function readRetailQtyValue(item) {
  const value = Number(item?.retailQty ?? item?.retail_qty ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function readRetailRateValue(item) {
  const value = Number(item?.retailRate ?? item?.retail_rate ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function readRetailAmountValue(item) {
  const explicit = Number(item?.retailAmount ?? item?.retail_amount);
  if (Number.isFinite(explicit)) return explicit;
  return +(readRetailQtyValue(item) * readRetailRateValue(item)).toFixed(2);
}

function getEntryTotalQty(entry) {
  const directValue = Number(entry?.totalQty);
  if (Number.isFinite(directValue)) return directValue;
  if (Array.isArray(entry?.items)) {
    return entry.items.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0);
  }
  return 0;
}

function buildPaymentDateSummary(entries, targetEntryIds) {
  const idSet = new Set((Array.isArray(targetEntryIds) ? targetEntryIds : []).map((id) => String(id)).filter(Boolean));
  if (!idSet.size) return [];

  const totalsByDate = new Map();

  for (const entry of Array.isArray(entries) ? entries : []) {
    const entryId = String(entry?.id || "");
    if (!idSet.has(entryId)) continue;

    for (const payment of Array.isArray(entry?.payments) ? entry.payments : []) {
      const paymentType = normalizeSettlementType(
        payment?.type || payment?.settlementType || payment?.transactionType
      );
      if (paymentType !== SETTLEMENT_TYPE_PAYMENT) continue;

      const paymentDate = normalizeYmd(payment?.date || payment?.created_at || payment?.createdAt);
      if (!paymentDate) continue;

      if (!totalsByDate.has(paymentDate)) {
        totalsByDate.set(paymentDate, {
          amount: 0,
          modes: new Set(),
        });
      }

      const bucket = totalsByDate.get(paymentDate);
      bucket.amount += Number(payment?.amount) || 0;

      const mode = String(payment?.mode || "").trim();
      if (mode) {
        bucket.modes.add(mode);
      }
    }
  }

  return Array.from(totalsByDate.entries())
    .map(([date, value]) => ({
      date,
      amount: +Number(value?.amount || 0).toFixed(2),
      modesText: Array.from(value?.modes || []).join(", "),
    }))
    .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
}

function buildPurchaseQtyBreakdown(entries, monthValue, itemGroupLookup, includeSupplierColumn) {
  const monthKey = normalizeYm(monthValue);
  if (!monthKey) {
    return {
      rows: [],
      weekTotals: [],
      totalQty: 0,
      totalAmount: 0,
      categoryCount: 0,
      dateCount: 0,
      includeSupplierColumn: !!includeSupplierColumn,
    };
  }

  const weekOptions = buildMonthWeekOptions(monthKey);
  const weekLookup = new Map(weekOptions.map((week) => [week.key, week]));
  const groupedRows = new Map();
  const weekTotalsMap = new Map();
  const seenCategories = new Set();
  const seenDates = new Set();
  let totalQty = 0;
  let totalAmount = 0;

  for (const entry of Array.isArray(entries) ? entries : []) {
    const supplierName = String(entry?.supplierName || "").trim() || "Unknown Supplier";
    for (const item of Array.isArray(entry?.items) ? entry.items : []) {
      const dateKey = normalizeYmd(item?.date || entry?.createdAt);
      if (!dateKey || !dateKey.startsWith(`${monthKey}-`)) continue;

      const materialKey = normalizeSearchText(item?.material);
      const category = String(itemGroupLookup.get(materialKey) || "").trim() || "Ungrouped";
      const qty = Number(item?.qty) || 0;
      const rawAmount = Number(item?.amount);
      const amount = Number.isFinite(rawAmount) ? rawAmount : qty * (Number(item?.rate) || 0);
      if (!qty && !amount) continue;

      const weekKey = startOfWeekMonday(dateKey);
      const weekInfo =
        weekLookup.get(weekKey) || {
          key: weekKey,
          index: 999,
          start: dateKey,
          end: dateKey,
          label: `Week ${formatDisplayDate(dateKey)}`,
        };
      const rowKey = [weekInfo.key, dateKey, category, includeSupplierColumn ? supplierName : ""].join("::");

      if (!groupedRows.has(rowKey)) {
        groupedRows.set(rowKey, {
          weekKey: weekInfo.key,
          weekOrder: weekInfo.index,
          weekLabel: weekInfo.label,
          date: dateKey,
          supplierName,
          category,
          qty: 0,
          amount: 0,
        });
      }

      const row = groupedRows.get(rowKey);
      row.qty += qty;
      row.amount += amount;

      if (!weekTotalsMap.has(weekInfo.key)) {
        weekTotalsMap.set(weekInfo.key, {
          weekKey: weekInfo.key,
          weekOrder: weekInfo.index,
          weekLabel: weekInfo.label,
          qty: 0,
          amount: 0,
        });
      }
      const weekBucket = weekTotalsMap.get(weekInfo.key);
      weekBucket.qty += qty;
      weekBucket.amount += amount;

      seenCategories.add(category);
      seenDates.add(dateKey);
      totalQty += qty;
      totalAmount += amount;
    }
  }

  const rows = Array.from(groupedRows.values())
    .map((row) => ({
      ...row,
      qty: +Number(row.qty || 0).toFixed(2),
      amount: +Number(row.amount || 0).toFixed(2),
    }))
    .sort((a, b) => {
      if (a.weekOrder !== b.weekOrder) return a.weekOrder - b.weekOrder;
      const dateCompare = String(a.date || "").localeCompare(String(b.date || ""));
      if (dateCompare !== 0) return dateCompare;
      if (includeSupplierColumn) {
        const supplierCompare = String(a.supplierName || "").localeCompare(String(b.supplierName || ""), undefined, {
          sensitivity: "base",
        });
        if (supplierCompare !== 0) return supplierCompare;
      }
      return String(a.category || "").localeCompare(String(b.category || ""), undefined, { sensitivity: "base" });
    });

  const weekTotals = Array.from(weekTotalsMap.values())
    .map((row) => ({
      ...row,
      qty: +Number(row.qty || 0).toFixed(2),
      amount: +Number(row.amount || 0).toFixed(2),
    }))
    .sort((a, b) => a.weekOrder - b.weekOrder);

  return {
    rows,
    weekTotals,
    totalQty: +totalQty.toFixed(2),
    totalAmount: +totalAmount.toFixed(2),
    categoryCount: seenCategories.size,
    dateCount: seenDates.size,
    includeSupplierColumn: !!includeSupplierColumn,
  };
}

function buildMaterialNames(items) {
  return Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => String(item?.material || "").trim())
        .filter(Boolean)
    )
  ).join(", ");
}

function normalizeItemGroupKey(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function resolveDeliveryBillingCategory(groupKey) {
  const normalized = normalizeItemGroupKey(groupKey);
  return DELIVERY_BILLING_CATEGORY_MAP[normalized] || DELIVERY_BILLING_GENERAL_CATEGORY;
}

function toMoneyCents(value) {
  return Math.max(0, Math.round((Number(value) || 0) * 100));
}

function distributeDiscountAcrossRows(itemAmounts, discountAmount) {
  const amounts = Array.isArray(itemAmounts) ? itemAmounts.map((value) => toMoneyCents(value)) : [];
  if (!amounts.length) return [];

  const totalAmountCents = amounts.reduce((sum, value) => sum + value, 0);
  const totalDiscountCents = Math.min(toMoneyCents(discountAmount), totalAmountCents);
  if (totalAmountCents <= 0 || totalDiscountCents <= 0) {
    return amounts.map(() => 0);
  }

  const baseShares = amounts.map((amount) => Math.floor((totalDiscountCents * amount) / totalAmountCents));
  let remainder = totalDiscountCents - baseShares.reduce((sum, value) => sum + value, 0);

  if (remainder > 0) {
    const priority = amounts
      .map((amount, index) => ({
        index,
        fraction: (totalDiscountCents * amount) % totalAmountCents,
        capacity: amount - baseShares[index],
      }))
      .filter((entry) => entry.capacity > 0)
      .sort((a, b) => {
        if (b.fraction !== a.fraction) return b.fraction - a.fraction;
        if (b.capacity !== a.capacity) return b.capacity - a.capacity;
        return a.index - b.index;
      });

    for (let i = 0; i < priority.length && remainder > 0; i += 1) {
      baseShares[priority[i].index] += 1;
      remainder -= 1;
    }
  }

  return baseShares;
}

function getSalesPulseBucketKey(entry) {
  const category = String(entry?.category || "").trim();
  if (category === "Billing") return "delivery-billing";
  if (category === "Stall") {
    return isEveningSalesSource(entry?.source) ? "stall-evening" : "stall-credit";
  }
  if (category === "Market") return "market-credit";
  return "";
}

function buildOverallBreakdownRows(entries, getLabel) {
  const grouped = new Map();

  for (const entry of Array.isArray(entries) ? entries : []) {
    const rawLabel = typeof getLabel === "function" ? getLabel(entry) : "";
    const label = String(rawLabel || "").trim() || "Unknown";
    const settlementSummary = getSettlementSummary(Number(entry?.total) || 0, entry?.payments);
    const totalAmount = settlementSummary.totalAmount;
    const paidAmount = settlementSummary.paidAmount;
    const discountAmount = settlementSummary.discountAmount;
    const knockOffAmount = settlementSummary.knockOffAmount;
    const balanceAmount = settlementSummary.pendingAmount;

    if (!grouped.has(label)) {
      grouped.set(label, {
        label,
        entries: 0,
        totalAmount: 0,
        paidAmount: 0,
        discountAmount: 0,
        knockOffAmount: 0,
        balanceAmount: 0,
      });
    }

    const bucket = grouped.get(label);
    bucket.entries += 1;
    bucket.totalAmount += totalAmount;
    bucket.paidAmount += paidAmount;
    bucket.discountAmount += discountAmount;
    bucket.knockOffAmount += knockOffAmount;
    bucket.balanceAmount += balanceAmount;
  }

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      totalAmount: +Number(row.totalAmount || 0).toFixed(2),
      paidAmount: +Number(row.paidAmount || 0).toFixed(2),
      discountAmount: +Number(row.discountAmount || 0).toFixed(2),
      knockOffAmount: +Number(row.knockOffAmount || 0).toFixed(2),
      balanceAmount: +Number(row.balanceAmount || 0).toFixed(2),
    }))
    .sort((a, b) => {
      const amountDiff = Number(b.totalAmount || 0) - Number(a.totalAmount || 0);
      if (amountDiff !== 0) return amountDiff;
      return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
    });
}

function buildOverallBalanceRows(entries, getLabel) {
  return buildOverallBreakdownRows(entries, getLabel).sort((a, b) => {
    const balanceDiff = Number(b.balanceAmount || 0) - Number(a.balanceAmount || 0);
    if (balanceDiff !== 0) return balanceDiff;
    const amountDiff = Number(b.totalAmount || 0) - Number(a.totalAmount || 0);
    if (amountDiff !== 0) return amountDiff;
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  });
}

function resolveOverallSelections(options, selections) {
  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];
  if (selections == null) return safeOptions;
  const allowed = new Set(safeOptions);
  return (Array.isArray(selections) ? selections : []).filter((value) => allowed.has(value));
}

function toggleSelectionValue(options, currentSelections, value) {
  const base = currentSelections == null ? resolveOverallSelections(options, null) : currentSelections;
  const target = String(value || "");
  const nextSet = new Set(base);
  if (nextSet.has(target)) {
    nextSet.delete(target);
  } else {
    nextSet.add(target);
  }
  return (Array.isArray(options) ? options : []).filter((option) => nextSet.has(option));
}

function SummaryChecklistDropdown({
  label,
  options,
  selectedValues,
  onToggleValue,
  onSelectAll,
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const selectedCount = Array.isArray(selectedValues) ? selectedValues.length : 0;
  const totalCount = Array.isArray(options) ? options.length : 0;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  useEffect(() => {
    if (!open) return undefined;

    const updateRect = () => {
      if (!triggerRef.current) return;
      const nextRect = triggerRef.current.getBoundingClientRect();
      setRect({
        top: nextRect.bottom + window.scrollY + 6,
        left: nextRect.left + window.scrollX,
        width: Math.max(nextRect.width, 280),
      });
    };

    const handlePointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <label className="supplier-summary-filter supplier-summary-filter--checklist">
      <span>{label}</span>
      <button
        type="button"
        ref={triggerRef}
        className="dashboard-checklist-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{allSelected ? `All Selected (${selectedCount})` : `${selectedCount} Selected`}</span>
        <span className={`dashboard-checklist-trigger__caret${open ? " is-open" : ""}`}>v</span>
      </button>
      {open && rect ? (
        <PortalDropdown
          className="dashboard-checklist-dropdown"
          style={{
            position: "absolute",
            top: rect.top,
            left: rect.left,
            width: rect.width,
          }}
        >
          <div ref={panelRef}>
            <div className="dashboard-checklist-dropdown__head">
              <strong>{label}</strong>
              <button type="button" className="dashboard-checklist-dropdown__link" onClick={onSelectAll}>
                Select All
              </button>
            </div>
            <div className="dashboard-checklist-dropdown__list">
              {options.length === 0 ? (
                <div className="dashboard-checklist-dropdown__empty">No options</div>
              ) : (
                options.map((option) => {
                  const checked = selectedValues.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`dashboard-checklist-option${checked ? " is-checked" : ""}`}
                      onClick={() => onToggleValue(option)}
                    >
                      <span className={`dashboard-checklist-option__box${checked ? " is-checked" : ""}`}>
                        {checked ? "✓" : ""}
                      </span>
                      <span className="dashboard-checklist-option__label">{option}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PortalDropdown>
      ) : null}
    </label>
  );
}

function buildWeeklyFinanceMatrix(entries, monthValue, getEntryDate) {
  const monthKey = normalizeYm(monthValue);
  const monthStart = startOfMonth(monthKey);
  const monthEnd = endOfMonth(monthKey);
  if (!monthKey || !monthStart || !monthEnd) {
    return {
      weeks: [],
      dayTotals: {},
      dayPaidTotals: {},
      dayDiscountTotals: {},
      dayKnockOffTotals: {},
      dayPendingTotals: {},
      dayTargetEntryIds: {},
      totalAmount: 0,
      totalQty: 0,
      totalPaidAmount: 0,
      totalDiscountAmount: 0,
      totalKnockOffAmount: 0,
      totalEntries: 0,
      totalPendingAmount: 0,
      monthTargetEntryIds: [],
    };
  }

  const metricsByDate = new Map();
  let totalAmount = 0;
  let totalQty = 0;
  let totalPaidAmount = 0;
  let totalDiscountAmount = 0;
  let totalKnockOffAmount = 0;
  let totalPendingAmount = 0;
  let totalEntries = 0;

  for (const entry of Array.isArray(entries) ? entries : []) {
    const dateKey = normalizeYmd(getEntryDate(entry));
    if (!dateKey || dateKey < monthStart || dateKey > monthEnd) continue;
    totalEntries += 1;
    const settlementSummary = getSettlementSummary(Number(entry?.total) || 0, entry?.payments);
    const amount = settlementSummary.totalAmount;
    const qty = getEntryTotalQty(entry);
    const paidAmount = settlementSummary.paidAmount;
    const discountAmount = settlementSummary.discountAmount;
    const knockOffAmount = settlementSummary.knockOffAmount;
    const pendingAmount = settlementSummary.pendingAmount;
    totalAmount += amount;
    totalQty += qty;
    totalPaidAmount += paidAmount;
    totalDiscountAmount += discountAmount;
    totalKnockOffAmount += knockOffAmount;
    totalPendingAmount += pendingAmount;
    if (!metricsByDate.has(dateKey)) {
      metricsByDate.set(dateKey, {
        amount: 0,
        paidAmount: 0,
        discountAmount: 0,
        knockOffAmount: 0,
        pendingAmount: 0,
        targetEntryIds: [],
      });
    }
    const bucket = metricsByDate.get(dateKey);
    bucket.amount += amount;
    bucket.paidAmount += paidAmount;
    bucket.discountAmount += discountAmount;
    bucket.knockOffAmount += knockOffAmount;
    bucket.pendingAmount += pendingAmount;
    if (entry?.id) {
      bucket.targetEntryIds.push(String(entry.id));
    }
  }

  const startWeek = startOfWeekMonday(monthStart);
  const endWeek = startOfWeekMonday(monthEnd);
  const weeks = [];
  const dayTotals = SUPPLIER_WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = 0;
    return acc;
  }, {});
  const dayPaidTotals = SUPPLIER_WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = 0;
    return acc;
  }, {});
  const dayDiscountTotals = SUPPLIER_WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = 0;
    return acc;
  }, {});
  const dayKnockOffTotals = SUPPLIER_WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = 0;
    return acc;
  }, {});
  const dayPendingTotals = SUPPLIER_WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = 0;
    return acc;
  }, {});
  const dayTargetEntryIds = SUPPLIER_WEEK_DAYS.reduce((acc, day) => {
    acc[day.key] = [];
    return acc;
  }, {});
  const monthTargetEntryIds = [];

  for (let cursor = startWeek; cursor && cursor <= endWeek; cursor = addDaysToYmd(cursor, 7)) {
    const values = {};
    let weekTotal = 0;
    let weekPaidTotal = 0;
    let weekDiscountTotal = 0;
    let weekKnockOffTotal = 0;
    let weekPendingTotal = 0;
    const weekTargetEntryIds = [];

    for (const day of SUPPLIER_WEEK_DAYS) {
      const dayDate = addDaysToYmd(cursor, day.offset);
      const cellMetric =
        dayDate &&
        dayDate >= monthStart &&
        dayDate <= monthEnd &&
        metricsByDate.has(dayDate)
          ? metricsByDate.get(dayDate)
          : null;
      const amount = cellMetric ? +Number(cellMetric.amount || 0).toFixed(2) : null;
      const paidAmount = cellMetric ? +Number(cellMetric.paidAmount || 0).toFixed(2) : 0;
      const discountAmount = cellMetric ? +Number(cellMetric.discountAmount || 0).toFixed(2) : 0;
      const knockOffAmount = cellMetric ? +Number(cellMetric.knockOffAmount || 0).toFixed(2) : 0;
      const pendingAmount = cellMetric ? +Number(cellMetric.pendingAmount || 0).toFixed(2) : 0;
      const targetEntryIds = cellMetric ? Array.from(new Set(cellMetric.targetEntryIds || [])) : [];
      values[day.key] = {
        amount,
        paidAmount,
        discountAmount,
        knockOffAmount,
        pendingAmount,
        targetEntryIds,
        date: dayDate || "",
      };
      if (amount != null) {
        weekTotal += Number(amount) || 0;
        dayTotals[day.key] += Number(amount) || 0;
      }
      if (paidAmount > 0) {
        weekPaidTotal += Number(paidAmount) || 0;
        dayPaidTotals[day.key] += Number(paidAmount) || 0;
      }
      if (discountAmount > 0) {
        weekDiscountTotal += Number(discountAmount) || 0;
        dayDiscountTotals[day.key] += Number(discountAmount) || 0;
      }
      if (knockOffAmount > 0) {
        weekKnockOffTotal += Number(knockOffAmount) || 0;
        dayKnockOffTotals[day.key] += Number(knockOffAmount) || 0;
      }
      if (pendingAmount > 0) {
        weekPendingTotal += Number(pendingAmount) || 0;
        dayPendingTotals[day.key] += Number(pendingAmount) || 0;
      }
      weekTargetEntryIds.push(...targetEntryIds);
      dayTargetEntryIds[day.key].push(...targetEntryIds);
      monthTargetEntryIds.push(...targetEntryIds);
    }

    weeks.push({
      key: cursor,
      start: cursor < monthStart ? monthStart : cursor,
      end: addDaysToYmd(cursor, 6) > monthEnd ? monthEnd : addDaysToYmd(cursor, 6),
      values,
      total: +weekTotal.toFixed(2),
      paidTotal: +weekPaidTotal.toFixed(2),
      discountTotal: +weekDiscountTotal.toFixed(2),
      knockOffTotal: +weekKnockOffTotal.toFixed(2),
      pendingTotal: +weekPendingTotal.toFixed(2),
      targetEntryIds: Array.from(new Set(weekTargetEntryIds)),
    });
  }

  return {
    weeks,
    dayTotals: Object.fromEntries(
      Object.entries(dayTotals).map(([key, value]) => [key, +Number(value || 0).toFixed(2)])
    ),
    dayPaidTotals: Object.fromEntries(
      Object.entries(dayPaidTotals).map(([key, value]) => [key, +Number(value || 0).toFixed(2)])
    ),
    dayDiscountTotals: Object.fromEntries(
      Object.entries(dayDiscountTotals).map(([key, value]) => [key, +Number(value || 0).toFixed(2)])
    ),
    dayKnockOffTotals: Object.fromEntries(
      Object.entries(dayKnockOffTotals).map(([key, value]) => [key, +Number(value || 0).toFixed(2)])
    ),
    dayPendingTotals: Object.fromEntries(
      Object.entries(dayPendingTotals).map(([key, value]) => [key, +Number(value || 0).toFixed(2)])
    ),
    dayTargetEntryIds: Object.fromEntries(
      Object.entries(dayTargetEntryIds).map(([key, value]) => [key, Array.from(new Set(value))])
    ),
    totalAmount: +totalAmount.toFixed(2),
    totalQty: +totalQty.toFixed(2),
    totalPaidAmount: +totalPaidAmount.toFixed(2),
    totalDiscountAmount: +totalDiscountAmount.toFixed(2),
    totalKnockOffAmount: +totalKnockOffAmount.toFixed(2),
    totalPendingAmount: +totalPendingAmount.toFixed(2),
    monthTargetEntryIds: Array.from(new Set(monthTargetEntryIds)),
    totalEntries,
  };
}

function buildLedgerSummary(entries, monthValue, getEntryDate) {
  const monthKey = normalizeYm(monthValue);
  const monthStart = startOfMonth(monthKey);
  const monthEnd = endOfMonth(monthKey);
  if (!monthKey || !monthStart || !monthEnd) {
    return {
      openingBalance: 0,
      closingBalance: 0,
      monthPurchaseTotal: 0,
      monthPaidTotal: 0,
      monthDiscountTotal: 0,
      monthKnockOffTotal: 0,
      balanceRows: [],
      historyRows: [],
    };
  }

  const byDate = new Map();
  const historyRows = [];

  const ensureDateBucket = (date) => {
    if (!date) return null;
    if (!byDate.has(date)) {
      byDate.set(date, { date, purchaseAmount: 0, paidAmount: 0, discountAmount: 0, knockOffAmount: 0 });
    }
    return byDate.get(date);
  };

  for (const entry of Array.isArray(entries) ? entries : []) {
    const purchaseDate = normalizeYmd(getEntryDate(entry));
    const purchaseAmount = Number(entry?.total) || 0;
    if (purchaseDate && purchaseDate <= monthEnd) {
      const bucket = ensureDateBucket(purchaseDate);
      if (bucket) {
        bucket.purchaseAmount += purchaseAmount;
      }
    }

    for (const payment of Array.isArray(entry?.payments) ? entry.payments : []) {
      const paymentDate = normalizeYmd(payment?.date || payment?.created_at || payment?.createdAt);
      const paymentAmount = Number(payment?.amount) || 0;
      if (!paymentDate || paymentAmount <= 0 || paymentDate > monthEnd) continue;
      const type = normalizeSettlementType(payment?.type || payment?.settlementType || payment?.transactionType);
      const paymentBucket = ensureDateBucket(paymentDate);
      if (paymentBucket) {
        if (type === "discount") {
          paymentBucket.discountAmount += paymentAmount;
        } else if (type === "knock_off") {
          paymentBucket.knockOffAmount += paymentAmount;
        } else {
          paymentBucket.paidAmount += paymentAmount;
        }
      }
      if (paymentDate >= monthStart) {
        historyRows.push({
          paymentId: String(payment?.id || ""),
          paymentDate,
          againstDate: purchaseDate || "",
          amount: +paymentAmount.toFixed(2),
          type,
          mode: String(payment?.mode || "").trim(),
          createdAt: String(payment?.created_at || payment?.createdAt || ""),
        });
      }
    }
  }

  const sortedDates = Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));
  let runningBalance = 0;
  let openingBalance = 0;
  let monthPurchaseTotal = 0;
  let monthPaidTotal = 0;
  let monthDiscountTotal = 0;
  let monthKnockOffTotal = 0;
  const balanceRows = [];

  for (const date of sortedDates) {
    const bucket = byDate.get(date);
    const purchaseAmount = +Number(bucket?.purchaseAmount || 0).toFixed(2);
    const paidAmount = +Number(bucket?.paidAmount || 0).toFixed(2);
    const discountAmount = +Number(bucket?.discountAmount || 0).toFixed(2);
    const knockOffAmount = +Number(bucket?.knockOffAmount || 0).toFixed(2);
    runningBalance = +(runningBalance + purchaseAmount - paidAmount - discountAmount - knockOffAmount).toFixed(2);

    if (date < monthStart) {
      openingBalance = runningBalance;
      continue;
    }
    if (date > monthEnd) {
      break;
    }

    monthPurchaseTotal += purchaseAmount;
    monthPaidTotal += paidAmount;
    monthDiscountTotal += discountAmount;
    monthKnockOffTotal += knockOffAmount;
    balanceRows.push({
      date,
      purchaseAmount,
      paidAmount,
      discountAmount,
      knockOffAmount,
      balanceAmount: runningBalance,
    });
  }

  historyRows.sort((a, b) => {
    const dateCmp = String(b.paymentDate || "").localeCompare(String(a.paymentDate || ""));
    if (dateCmp !== 0) return dateCmp;
    const createdCmp = String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
    if (createdCmp !== 0) return createdCmp;
    return String(b.paymentId || "").localeCompare(String(a.paymentId || ""));
  });

  return {
    openingBalance: +openingBalance.toFixed(2),
    closingBalance: +runningBalance.toFixed(2),
    monthPurchaseTotal: +monthPurchaseTotal.toFixed(2),
    monthPaidTotal: +monthPaidTotal.toFixed(2),
    monthDiscountTotal: +monthDiscountTotal.toFixed(2),
    monthKnockOffTotal: +monthKnockOffTotal.toFixed(2),
    balanceRows,
    historyRows,
  };
}

function paymentTypeClassName(value) {
  return String(value || "Any")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function Dashboard() {
  const currentDateKey = toLocalYmd(new Date());
  const currentMonthKey = currentDateKey.slice(0, 7);
  const [entries, setEntries] = useState([]);
  const [inwardEntries, setInwardEntries] = useState([]);
  const [itemMasterEntries, setItemMasterEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [salesBreakdownPage, setSalesBreakdownPage] = useState(1);
  const [salesPaymentDetailsPage, setSalesPaymentDetailsPage] = useState(1);
  const [salesFromDate, setSalesFromDate] = useState(currentDateKey);
  const [salesToDate, setSalesToDate] = useState(currentDateKey);
  const [salesSegmentFilter, setSalesSegmentFilter] = useState("All");
  const [salesBucketFilter, setSalesBucketFilter] = useState("all");
  const [salesPaymentTypeFilter, setSalesPaymentTypeFilter] = useState("All");
  const [salesPartySearch, setSalesPartySearch] = useState("");
  const [salesMaterialSearch, setSalesMaterialSearch] = useState("");
  const [activeTab, setActiveTab] = useState("stall-credit-summary");
  const [selectedSupplierName, setSelectedSupplierName] = useState("");
  const [selectedStallCustomerName, setSelectedStallCustomerName] = useState("");
  const [selectedMarketCustomerName, setSelectedMarketCustomerName] = useState("");
  const [selectedSupplierOverallNames, setSelectedSupplierOverallNames] = useState(null);
  const [selectedStallOverallCustomers, setSelectedStallOverallCustomers] = useState(null);
  const [selectedMarketOverallCustomers, setSelectedMarketOverallCustomers] = useState(null);
  const [selectedSupplierMonth, setSelectedSupplierMonth] = useState(currentMonthKey);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [monthPickerYear, setMonthPickerYear] = useState(Number(currentMonthKey.slice(0, 4)));
  const [selectedSupplierWeekKey, setSelectedSupplierWeekKey] = useState("");
  const [selectedStallWeekKey, setSelectedStallWeekKey] = useState("");
  const [selectedMarketWeekKey, setSelectedMarketWeekKey] = useState("");
  const [supplierSettlementHistoryPage, setSupplierSettlementHistoryPage] = useState(1);
  const [stallSettlementHistoryPage, setStallSettlementHistoryPage] = useState(1);
  const [marketSettlementHistoryPage, setMarketSettlementHistoryPage] = useState(1);
  const [supplierRevertingSettlementId, setSupplierRevertingSettlementId] = useState("");
  const [stallRevertingSettlementId, setStallRevertingSettlementId] = useState("");
  const [marketRevertingSettlementId, setMarketRevertingSettlementId] = useState("");
  const [supplierOverallBreakdown, setSupplierOverallBreakdown] = useState(() => makeOverallBreakdownState());
  const [stallOverallBreakdown, setStallOverallBreakdown] = useState(() => makeOverallBreakdownState());
  const [marketOverallBreakdown, setMarketOverallBreakdown] = useState(() => makeOverallBreakdownState());
  const [overallBalanceModal, setOverallBalanceModal] = useState(() => makeOverallBalanceModalState());
  const [overallBalancePage, setOverallBalancePage] = useState(1);
  const [overallBalanceFilter, setOverallBalanceFilter] = useState("");
  const [overallBalanceVisibleColumns, setOverallBalanceVisibleColumns] = useState(() =>
    makeOverallBalanceVisibleColumns()
  );
  const [supplierQtyBreakdownOpen, setSupplierQtyBreakdownOpen] = useState(false);
  const [supplierPaymentModal, setSupplierPaymentModal] = useState({
    open: false,
    viewOnly: false,
    label: "",
    supplierName: "",
    totalAmount: 0,
    paidAmount: 0,
    discountAmount: 0,
    knockOffAmount: 0,
    pendingAmount: 0,
    paidDateSummary: [],
    targetEntryIds: [],
  });
  const [supplierPaymentForm, setSupplierPaymentForm] = useState(() => makeSupplierPaymentForm());
  const [supplierPaymentSaving, setSupplierPaymentSaving] = useState(false);
  const [stallCreditPaymentModal, setStallCreditPaymentModal] = useState({
    open: false,
    viewOnly: false,
    label: "",
    customerName: "",
    totalAmount: 0,
    paidAmount: 0,
    discountAmount: 0,
    knockOffAmount: 0,
    pendingAmount: 0,
    paidDateSummary: [],
    targetEntryIds: [],
  });
  const [stallCreditPaymentForm, setStallCreditPaymentForm] = useState(() => makeSupplierPaymentForm());
  const [stallCreditPaymentSaving, setStallCreditPaymentSaving] = useState(false);
  const [marketCreditPaymentModal, setMarketCreditPaymentModal] = useState({
    open: false,
    viewOnly: false,
    label: "",
    customerName: "",
    totalAmount: 0,
    paidAmount: 0,
    discountAmount: 0,
    knockOffAmount: 0,
    pendingAmount: 0,
    paidDateSummary: [],
    targetEntryIds: [],
  });
  const [marketCreditPaymentForm, setMarketCreditPaymentForm] = useState(() => makeSupplierPaymentForm());
  const [marketCreditPaymentSaving, setMarketCreditPaymentSaving] = useState(false);
  const monthFilterRef = useRef(null);

  useEffect(() => {
    if (!monthPickerOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!monthFilterRef.current?.contains(event.target)) setMonthPickerOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setMonthPickerOpen(false);
      monthFilterRef.current?.querySelector(".dashboard-date-filter__trigger")?.focus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [monthPickerOpen]);

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const [salesList, inwardList, itemList] = await Promise.all([getSalesEntries(), getInwardEntries(), getItems()]);
      setEntries(Array.isArray(salesList) ? salesList : []);
      setInwardEntries(Array.isArray(inwardList) ? inwardList : []);
      setItemMasterEntries(Array.isArray(itemList) ? itemList : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const salesEntries = useMemo(
    () =>
      (Array.isArray(entries) ? entries : []).filter((en) =>
        ["Billing", "Stall"].includes(String(en?.category || ""))
      ),
    [entries]
  );

  const salesPulseRows = useMemo(() => {
    const rows = [];
    const deliveryRetailDeductionByDate = new Map();
    const stallRetailByDate = new Map();

    for (const entry of salesEntries) {
      const bucketKey = getSalesPulseBucketKey(entry);
      const bucketMeta = SALES_PULSE_BUCKET_MAP[bucketKey];
      if (!bucketMeta) continue;
      const items = Array.isArray(entry?.items) ? entry.items : [];
      const discountShares =
        bucketKey === "delivery-billing"
          ? distributeDiscountAcrossRows(
              items.map((item) => Number(item?.amount) || 0),
              entry?.discountAmount || 0
            )
          : [];
      if (bucketKey === "delivery-billing") {
        items.forEach((item, index) => {
          const groupKey = normalizeItemGroupKey(item?.itemGroup || item?.item_group);
          if (!STALL_RETAIL_DELIVERY_GROUP_SET.has(groupKey)) return;
          const dateKey = normalizeYmd(entry?.saleDate);
          if (!dateKey) return;
          const qty = Number(item?.qty) || 0;
          const grossAmount = Number(item?.amount) || 0;
          const discountAmount = Number((discountShares[index] || 0) / 100) || 0;
          const netAmount = +(grossAmount - discountAmount).toFixed(2);
          if (netAmount <= 0 && qty <= 0) return;
          const currentBucket = deliveryRetailDeductionByDate.get(dateKey) || { amount: 0, qty: 0 };
          deliveryRetailDeductionByDate.set(dateKey, {
            amount: +((Number(currentBucket.amount) || 0) + netAmount).toFixed(2),
            qty: +((Number(currentBucket.qty) || 0) + qty).toFixed(3),
          });
        });
      }
      const materialsText = buildMaterialNames(items);
      const partyLabel = dashboardCustomerLabel(entry?.customer);
      const reference =
        String(entry?.billNo || "").trim() ||
        (bucketKey === "stall-evening" ? "Evening Sales" : bucketMeta.label);
      const deliveryItems =
        bucketKey === "delivery-billing"
          ? items.map((item, index) => {
              const grossAmount = Number(item?.amount) || 0;
              const discountAmount = Number((discountShares[index] || 0) / 100) || 0;
              return {
                material: String(item?.material || "").trim(),
                groupKey: normalizeItemGroupKey(item?.itemGroup || item?.item_group),
                groupLabel: String(item?.itemGroup || item?.item_group || "").trim(),
                qty: +(Number(item?.qty) || 0).toFixed(3),
                amount: +(grossAmount - discountAmount).toFixed(2),
              };
            })
          : [];
      rows.push({
        id: `sales-${String(entry?.id || "")}`,
        rowType: "sales",
        rawId: String(entry?.id || ""),
        bucketKey,
        bucketLabel: bucketMeta.label,
        segment: bucketMeta.segment,
        accent: bucketMeta.accent,
        soft: bucketMeta.soft,
        date: normalizeYmd(entry?.saleDate),
        reference,
        party: partyLabel,
        paymentType: String(entry?.paymentType || "Any").trim() || "Any",
        qty: +(Number(entry?.totalQty) || 0).toFixed(3),
        amount: +(Number(entry?.total) || 0).toFixed(2),
        itemCount: items.length,
        materialsText,
        rawCustomer: String(entry?.customer || ""),
        deliveryItems,
      });
    }

    for (const entry of Array.isArray(inwardEntries) ? inwardEntries : []) {
      const category = String(entry?.category || "").trim();
      if (category !== "Stall" && category !== "Market") continue;

      const retailItems = (Array.isArray(entry?.items) ? entry.items : [])
        .map((item) => {
          const retailQty = readRetailQtyValue(item);
          const retailAmount = readRetailAmountValue(item);
          return {
            material: String(item?.material || "").trim(),
            retailQty,
            retailAmount,
          };
        })
        .filter((item) => item.material && (item.retailQty > 0 || item.retailAmount > 0));

      if (!retailItems.length) continue;

      const totalQty = retailItems.reduce((sum, item) => sum + (Number(item?.retailQty) || 0), 0);
      const totalAmount = retailItems.reduce((sum, item) => sum + (Number(item?.retailAmount) || 0), 0);
      const supplierName = String(entry?.supplierName || "").trim();
      const dateFromItems =
        normalizeYmd(retailItems[0]?.date) ||
        normalizeYmd(Array.isArray(entry?.items) ? entry.items[0]?.date : "") ||
        normalizeYmd(entry?.createdAt);

      if (category === "Stall") {
        const dateKey = dateFromItems;
        if (!dateKey) continue;
        if (!stallRetailByDate.has(dateKey)) {
          stallRetailByDate.set(dateKey, {
            date: dateKey,
            qty: 0,
            actualAmount: 0,
            itemCount: 0,
            suppliers: new Set(),
            materials: new Set(),
          });
        }
        const bucket = stallRetailByDate.get(dateKey);
        bucket.qty += totalQty;
        bucket.actualAmount += totalAmount;
        bucket.itemCount += retailItems.length;
        if (supplierName) {
          bucket.suppliers.add(supplierName);
        }
        retailItems.forEach((item) => {
          if (item.material) {
            bucket.materials.add(item.material);
          }
        });
        continue;
      }

      const bucketKey = "market-retail";
      const bucketMeta = SALES_PULSE_BUCKET_MAP[bucketKey];
      const materialsText = buildMaterialNames(retailItems);

      rows.push({
        id: `retail-${category.toLowerCase()}-${String(entry?.id || "")}`,
        rowType: "retail",
        rawId: String(entry?.id || ""),
        bucketKey,
        bucketLabel: bucketMeta.label,
        segment: bucketMeta.segment,
        accent: bucketMeta.accent,
        soft: bucketMeta.soft,
        date: dateFromItems,
        reference: supplierName || bucketMeta.label,
        party: `${bucketMeta.segment} Retail Counter`,
        paymentType: "Retail",
        qty: +totalQty.toFixed(3),
        amount: +totalAmount.toFixed(2),
        itemCount: retailItems.length,
        materialsText,
        rawCustomer: supplierName,
      });
    }

    for (const [dateKey, retailBucket] of stallRetailByDate.entries()) {
      const bucketMeta = SALES_PULSE_BUCKET_MAP["stall-retail"];
      const actualQty = +Number(retailBucket.qty || 0).toFixed(3);
      const actualAmount = +Number(retailBucket.actualAmount || 0).toFixed(2);
      const deductionBucket = deliveryRetailDeductionByDate.get(dateKey) || { amount: 0, qty: 0 };
      const deductionAmount = +(Number(deductionBucket.amount) || 0).toFixed(2);
      const deductionQty = +Number(deductionBucket.qty || 0).toFixed(3);
      const netQty = +Math.max(0, actualQty - deductionQty).toFixed(3);
      const netAmount = +(actualAmount - deductionAmount).toFixed(2);
      const suppliers = Array.from(retailBucket.suppliers);
      rows.push({
        id: `retail-stall-${dateKey}`,
        rowType: "retail-adjusted",
        rawId: dateKey,
        bucketKey: "stall-retail",
        bucketLabel: bucketMeta.label,
        segment: bucketMeta.segment,
        accent: bucketMeta.accent,
        soft: bucketMeta.soft,
        date: dateKey,
        reference: suppliers.length ? suppliers.join(", ") : bucketMeta.label,
        party: "Stall Retail Counter",
        paymentType: "Retail",
        qty: netQty,
        amount: netAmount,
        actualQty,
        deliveryDeductionQty: deductionQty,
        netQty,
        actualAmount,
        deliveryDeductionAmount: deductionAmount,
        netAmount,
        itemCount: Number(retailBucket.itemCount) || 0,
        materialsText: Array.from(retailBucket.materials).join(", "),
        rawCustomer: suppliers.join(", "),
      });
    }

    return rows.sort((a, b) => {
      const dateCmp = String(b.date || "").localeCompare(String(a.date || ""));
      if (dateCmp !== 0) return dateCmp;
      const amountCmp = Number(b.amount || 0) - Number(a.amount || 0);
      if (amountCmp !== 0) return amountCmp;
      return String(a.bucketLabel || "").localeCompare(String(b.bucketLabel || ""));
    });
  }, [salesEntries, inwardEntries]);

  const salesRangeStart = useMemo(() => {
    const from = normalizeYmd(salesFromDate) || currentDateKey;
    const to = normalizeYmd(salesToDate) || from;
    return from <= to ? from : to;
  }, [currentDateKey, salesFromDate, salesToDate]);

  const salesRangeEnd = useMemo(() => {
    const from = normalizeYmd(salesFromDate) || currentDateKey;
    const to = normalizeYmd(salesToDate) || from;
    return from <= to ? to : from;
  }, [currentDateKey, salesFromDate, salesToDate]);

  const salesPaymentTypeOptions = useMemo(
    () =>
      ["All"].concat(
        Array.from(new Set(salesPulseRows.map((row) => String(row?.paymentType || "").trim()).filter(Boolean))).sort(
          (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })
        )
      ),
    [salesPulseRows]
  );

  const salesPulseBaseRows = useMemo(() => {
    const partyQuery = normalizeSearchText(salesPartySearch);
    const materialQuery = normalizeSearchText(salesMaterialSearch);

    return salesPulseRows.filter((row) => {
      const rowDate = normalizeYmd(row?.date);
      if (!rowDate || rowDate < salesRangeStart || rowDate > salesRangeEnd) return false;
      if (salesSegmentFilter !== "All" && String(row?.segment || "") !== salesSegmentFilter) return false;
      if (salesPaymentTypeFilter !== "All" && String(row?.paymentType || "") !== salesPaymentTypeFilter) return false;

      const searchableParty = normalizeSearchText(`${row?.party || ""} ${row?.reference || ""} ${row?.rawCustomer || ""}`);
      if (partyQuery && !searchableParty.includes(partyQuery)) return false;

      const searchableMaterials = normalizeSearchText(row?.materialsText || "");
      if (materialQuery && !searchableMaterials.includes(materialQuery)) return false;

      return true;
    });
  }, [
    salesPulseRows,
    salesRangeStart,
    salesRangeEnd,
    salesSegmentFilter,
    salesPaymentTypeFilter,
    salesPartySearch,
    salesMaterialSearch,
  ]);

  const salesPulseCards = useMemo(
    () =>
      SALES_PULSE_BUCKETS.map((bucket) => {
        const bucketRows = salesPulseBaseRows.filter((row) => row.bucketKey === bucket.key);
        return {
          ...bucket,
          entries: bucketRows.length,
          qty: +bucketRows.reduce((sum, row) => sum + (Number(row?.qty) || 0), 0).toFixed(3),
          amount: +bucketRows.reduce((sum, row) => sum + (Number(row?.amount) || 0), 0).toFixed(2),
        };
      }),
    [salesPulseBaseRows]
  );

  const filteredSalesPulseRows = useMemo(
    () =>
      salesBucketFilter === "all"
        ? salesPulseBaseRows
        : salesPulseBaseRows.filter((row) => row.bucketKey === salesBucketFilter),
    [salesPulseBaseRows, salesBucketFilter]
  );

  const stallRetailSalesBreakup = useMemo(() => {
    if (salesBucketFilter !== "stall-retail") return null;
    const stallRetailRows = filteredSalesPulseRows.filter((row) => row.bucketKey === "stall-retail");
    if (!stallRetailRows.length) return null;
    return stallRetailRows.reduce(
      (acc, row) => {
        acc.actualQty += Number(row?.actualQty ?? row?.qty) || 0;
        acc.deliveryDeductionQty += Number(row?.deliveryDeductionQty || 0) || 0;
        acc.netQty += Number(row?.netQty ?? row?.qty) || 0;
        acc.actualAmount += Number(row?.actualAmount ?? row?.amount) || 0;
        acc.deliveryDeductionAmount += Number(row?.deliveryDeductionAmount || 0) || 0;
        acc.netAmount += Number(row?.netAmount ?? row?.amount) || 0;
        acc.rows += 1;
        return acc;
      },
      { actualQty: 0, deliveryDeductionQty: 0, netQty: 0, actualAmount: 0, deliveryDeductionAmount: 0, netAmount: 0, rows: 0 }
    );
  }, [filteredSalesPulseRows, salesBucketFilter]);

  const isDeliveryBillingCategoryView = salesBucketFilter === "delivery-billing";
  const isCreditSalesPaymentView =
    salesBucketFilter === "stall-credit" || salesBucketFilter === "market-credit";

  const deliveryBillingCategoryRows = useMemo(() => {
    if (!isDeliveryBillingCategoryView) return [];

    const deliveryMeta = SALES_PULSE_BUCKET_MAP["delivery-billing"];
    const materialQuery = normalizeSearchText(salesMaterialSearch);
    const grouped = new Map(
      DELIVERY_BILLING_CATEGORY_DEFINITIONS.map((category) => [
        category.key,
        {
          id: `delivery-category-${category.key}`,
          rowType: "delivery-category",
          categoryLabel: category.label,
          categoryGroups: category.groups.join(", "),
          billIds: new Set(),
          itemCount: 0,
          qty: 0,
          amount: 0,
          accent: deliveryMeta.accent,
          soft: deliveryMeta.soft,
        },
      ])
    );

    filteredSalesPulseRows
      .filter((row) => row.bucketKey === "delivery-billing")
      .forEach((row) => {
        const items = Array.isArray(row?.deliveryItems) ? row.deliveryItems : [];
        items.forEach((item) => {
          const searchableMaterial = normalizeSearchText(`${item?.material || ""} ${item?.groupLabel || ""}`);
          if (materialQuery && !searchableMaterial.includes(materialQuery)) return;

          const category = resolveDeliveryBillingCategory(item?.groupKey);
          const bucket = grouped.get(category.key);
          if (!bucket) return;
          bucket.billIds.add(String(row?.rawId || row?.id || ""));
          bucket.itemCount += 1;
          bucket.qty += Number(item?.qty) || 0;
          bucket.amount += Number(item?.amount) || 0;
        });
      });

    return DELIVERY_BILLING_CATEGORY_DEFINITIONS.map((category) => {
      const bucket = grouped.get(category.key);
      return {
        id: bucket.id,
        rowType: bucket.rowType,
        categoryLabel: bucket.categoryLabel,
        categoryGroups: bucket.categoryGroups,
        billCount: bucket.billIds.size,
        itemCount: bucket.itemCount,
        qty: +Number(bucket.qty || 0).toFixed(3),
        amount: +Number(bucket.amount || 0).toFixed(2),
        accent: bucket.accent,
        soft: bucket.soft,
      };
    });
  }, [filteredSalesPulseRows, isDeliveryBillingCategoryView, salesMaterialSearch]);

  const salesPulseDisplayRows = useMemo(
    () => (isDeliveryBillingCategoryView ? deliveryBillingCategoryRows : filteredSalesPulseRows),
    [deliveryBillingCategoryRows, filteredSalesPulseRows, isDeliveryBillingCategoryView]
  );

  const salesPulsePaymentRows = useMemo(() => {
    if (!isCreditSalesPaymentView) return [];

    const grouped = new Map();
    const bucketMeta = SALES_PULSE_BUCKET_MAP[salesBucketFilter];
    const partyQuery = normalizeSearchText(salesPartySearch);
    const materialQuery = normalizeSearchText(salesMaterialSearch);

    for (const entry of Array.isArray(salesEntries) ? salesEntries : []) {
      if (getSalesPulseBucketKey(entry) !== salesBucketFilter) continue;
      if (!bucketMeta) continue;
      if (salesSegmentFilter !== "All" && String(bucketMeta.segment || "") !== salesSegmentFilter) continue;

      const entryPaymentType = String(entry?.paymentType || "Any").trim() || "Any";
      if (salesPaymentTypeFilter !== "All" && entryPaymentType !== salesPaymentTypeFilter) continue;

      const items = Array.isArray(entry?.items) ? entry.items : [];
      const materialsText = buildMaterialNames(items);
      const partyLabel = dashboardCustomerLabel(entry?.customer);
      const reference = String(entry?.billNo || "").trim() || bucketMeta.label;
      const searchableParty = normalizeSearchText(`${partyLabel} ${reference} ${entry?.customer || ""}`);
      if (partyQuery && !searchableParty.includes(partyQuery)) continue;
      if (materialQuery && !normalizeSearchText(materialsText).includes(materialQuery)) continue;

      const saleDate = normalizeYmd(entry?.saleDate);

      for (const payment of Array.isArray(entry?.payments) ? entry.payments : []) {
        if (!isPaymentSettlement(payment)) continue;

        const paymentDate = normalizeYmd(payment?.date || payment?.created_at || payment?.createdAt);
        const paymentAmount = Number(payment?.amount) || 0;
        if (!paymentDate || paymentAmount <= 0) continue;
        if (paymentDate < salesRangeStart || paymentDate > salesRangeEnd) continue;

        const key = `${paymentDate}::${partyLabel}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            id: key,
            paymentDate,
            customer: partyLabel,
            saleDates: new Set(),
            paymentModes: new Set(),
            paymentCount: 0,
            amount: 0,
          });
        }

        const bucket = grouped.get(key);
        if (saleDate) {
          bucket.saleDates.add(saleDate);
        }
        const mode = String(payment?.mode || "").trim();
        if (mode) {
          bucket.paymentModes.add(mode);
        }
        bucket.paymentCount += 1;
        bucket.amount += paymentAmount;
      }
    }

    return Array.from(grouped.values())
      .map((row) => {
        const relatedSalesDates = Array.from(row.saleDates).sort((a, b) => a.localeCompare(b));
        return {
          id: row.id,
          paymentDate: row.paymentDate,
          customer: row.customer,
          relatedSalesDates,
          relatedSalesDatesText: relatedSalesDates.map((date) => formatDisplayDate(date)).join(", "),
          paymentModesText: Array.from(row.paymentModes).join(", ") || "-",
          paymentCount: Number(row.paymentCount || 0),
          amount: +Number(row.amount || 0).toFixed(2),
        };
      })
      .sort((a, b) => {
        const dateCmp = String(b.paymentDate || "").localeCompare(String(a.paymentDate || ""));
        if (dateCmp !== 0) return dateCmp;
        const customerCmp = String(a.customer || "").localeCompare(String(b.customer || ""), undefined, {
          sensitivity: "base",
        });
        if (customerCmp !== 0) return customerCmp;
        return Number(b.amount || 0) - Number(a.amount || 0);
      });
  }, [
    isCreditSalesPaymentView,
    salesBucketFilter,
    salesEntries,
    salesMaterialSearch,
    salesPartySearch,
    salesPaymentTypeFilter,
    salesRangeEnd,
    salesRangeStart,
    salesSegmentFilter,
  ]);

  const salesPulsePaymentTotals = useMemo(() => {
    const customers = new Set();
    let amount = 0;
    let paymentCount = 0;

    for (const row of salesPulsePaymentRows) {
      const customer = String(row.customer || "").trim();
      if (customer) {
        customers.add(customer);
      }
      amount += Number(row.amount || 0);
      paymentCount += Number(row.paymentCount || 0);
    }

    return {
      amount: +Number(amount || 0).toFixed(2),
      paymentCount,
      customerCount: customers.size,
    };
  }, [salesPulsePaymentRows]);

  const salesPulseTotals = useMemo(() => {
    return filteredSalesPulseRows.reduce(
      (acc, row) => {
        acc.amount += Number(row?.amount) || 0;
        acc.qty += Number(row?.qty) || 0;
        acc.entries += 1;
        return acc;
      },
      { amount: 0, qty: 0, entries: 0 }
    );
  }, [filteredSalesPulseRows]);

  const salesBreakdownTotalPages = Math.max(
    1,
    Math.ceil(salesPulseDisplayRows.length / SALES_BREAKDOWN_PAGE_SIZE)
  );

  const pagedSelectedEntries = useMemo(() => {
    const startIndex = (salesBreakdownPage - 1) * SALES_BREAKDOWN_PAGE_SIZE;
    return salesPulseDisplayRows.slice(startIndex, startIndex + SALES_BREAKDOWN_PAGE_SIZE);
  }, [salesPulseDisplayRows, salesBreakdownPage]);

  const salesBreakdownPageStart =
    salesPulseDisplayRows.length === 0 ? 0 : (salesBreakdownPage - 1) * SALES_BREAKDOWN_PAGE_SIZE + 1;
  const salesBreakdownPageEnd =
    salesPulseDisplayRows.length === 0
      ? 0
      : Math.min(salesBreakdownPage * SALES_BREAKDOWN_PAGE_SIZE, salesPulseDisplayRows.length);

  const salesPaymentDetailsTotalPages = Math.max(
    1,
    Math.ceil(salesPulsePaymentRows.length / SALES_PAYMENT_DETAILS_PAGE_SIZE)
  );

  const pagedSalesPaymentRows = useMemo(() => {
    const startIndex = (salesPaymentDetailsPage - 1) * SALES_PAYMENT_DETAILS_PAGE_SIZE;
    return salesPulsePaymentRows.slice(startIndex, startIndex + SALES_PAYMENT_DETAILS_PAGE_SIZE);
  }, [salesPaymentDetailsPage, salesPulsePaymentRows]);

  const salesPaymentDetailsPageStart =
    salesPulsePaymentRows.length === 0 ? 0 : (salesPaymentDetailsPage - 1) * SALES_PAYMENT_DETAILS_PAGE_SIZE + 1;
  const salesPaymentDetailsPageEnd =
    salesPulsePaymentRows.length === 0
      ? 0
      : Math.min(salesPaymentDetailsPage * SALES_PAYMENT_DETAILS_PAGE_SIZE, salesPulsePaymentRows.length);

  useEffect(() => {
    setSalesBreakdownPage(1);
    setSalesPaymentDetailsPage(1);
  }, [
    salesFromDate,
    salesToDate,
    salesSegmentFilter,
    salesBucketFilter,
    salesPaymentTypeFilter,
    salesPartySearch,
    salesMaterialSearch,
  ]);

  useEffect(() => {
    setSalesBreakdownPage((current) => Math.min(current, salesBreakdownTotalPages));
  }, [salesBreakdownTotalPages]);

  useEffect(() => {
    setSalesPaymentDetailsPage((current) => Math.min(current, salesPaymentDetailsTotalPages));
  }, [salesPaymentDetailsTotalPages]);

  useEffect(() => {
    if (salesBucketFilter === "all") return;
    if (!salesPulseCards.some((card) => card.key === salesBucketFilter && card.entries > 0)) {
      setSalesBucketFilter("all");
    }
  }, [salesBucketFilter, salesPulseCards]);

  const materialPurchaseEntries = useMemo(
    () => (Array.isArray(inwardEntries) ? inwardEntries : []).filter((entry) => isMaterialPurchaseEntry(entry)),
    [inwardEntries]
  );

  const purchaseItemGroupLookup = useMemo(() => {
    const safeItems = Array.isArray(itemMasterEntries) ? itemMasterEntries : [];
    const purchaseItems = safeItems.filter(
      (item) => String(item?.itemType || "").trim().toLowerCase() === "purchase"
    );
    const sourceItems = purchaseItems.length ? purchaseItems : safeItems;
    const lookup = new Map();
    sourceItems.forEach((item) => {
      const nameKey = normalizeSearchText(item?.name);
      if (!nameKey) return;
      const itemGroup = String(item?.itemGroup || "").trim();
      if (itemGroup) {
        lookup.set(nameKey, itemGroup);
      }
    });
    return lookup;
  }, [itemMasterEntries]);

  const supplierOptions = useMemo(() => {
    return Array.from(
      new Set(
        materialPurchaseEntries
          .map((entry) => String(entry?.supplierName || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [materialPurchaseEntries]);

  const selectedSupplierOverallValues = useMemo(
    () => resolveOverallSelections(supplierOptions, selectedSupplierOverallNames),
    [supplierOptions, selectedSupplierOverallNames]
  );

  const handleSelectSupplierName = (value) => {
    setSelectedSupplierName(value);
    setSupplierOverallBreakdown(makeOverallBreakdownState());
    if (isOverallFilter(value)) {
      setSelectedSupplierOverallNames(null);
    }
  };

  const toggleSupplierOverallValue = (value) => {
    setSelectedSupplierOverallNames((prev) => toggleSelectionValue(supplierOptions, prev, value));
    setSupplierOverallBreakdown(makeOverallBreakdownState());
  };

  useEffect(() => {
    if (supplierOptions.length === 0) {
      setSelectedSupplierName("");
      setSelectedSupplierOverallNames(null);
      return;
    }
    if (!isOverallFilter(selectedSupplierName) && !supplierOptions.includes(selectedSupplierName)) {
      setSelectedSupplierName(supplierOptions[0] || "");
    }
  }, [supplierOptions, selectedSupplierName]);

  useEffect(() => {
    setSelectedSupplierOverallNames((prev) => (prev == null ? null : resolveOverallSelections(supplierOptions, prev)));
  }, [supplierOptions]);

  const supplierEntriesForSummary = useMemo(() => {
    const monthKey = normalizeYm(selectedSupplierMonth);
    if (!selectedSupplierName || !monthKey) return [];
    return materialPurchaseEntries
      .filter(
        (entry) =>
          (isOverallFilter(selectedSupplierName)
            ? selectedSupplierOverallValues.includes(String(entry?.supplierName || "").trim())
            : String(entry?.supplierName || "").trim() === selectedSupplierName) &&
          normalizeYmd(entry?.createdAt) &&
          normalizeYmd(entry?.createdAt).startsWith(`${monthKey}-`)
      )
      .sort((a, b) => normalizeYmd(a?.createdAt).localeCompare(normalizeYmd(b?.createdAt)));
  }, [materialPurchaseEntries, selectedSupplierMonth, selectedSupplierName, selectedSupplierOverallValues]);

  const supplierSummaryMatrix = useMemo(() => {
    return buildWeeklyFinanceMatrix(supplierEntriesForSummary, selectedSupplierMonth, (entry) => entry?.createdAt);
  }, [selectedSupplierMonth, supplierEntriesForSummary]);

  const supplierPurchaseQtyBreakdown = useMemo(
    () =>
      buildPurchaseQtyBreakdown(
        supplierEntriesForSummary,
        selectedSupplierMonth,
        purchaseItemGroupLookup,
        isOverallFilter(selectedSupplierName)
      ),
    [purchaseItemGroupLookup, selectedSupplierMonth, selectedSupplierName, supplierEntriesForSummary]
  );

  const supplierLedgerEntries = useMemo(
    () =>
      materialPurchaseEntries.filter(
        (entry) =>
          isOverallFilter(selectedSupplierName)
            ? selectedSupplierOverallValues.includes(String(entry?.supplierName || "").trim())
            : String(entry?.supplierName || "").trim() === selectedSupplierName
      ),
    [materialPurchaseEntries, selectedSupplierName, selectedSupplierOverallValues]
  );

  const supplierLedgerSummary = useMemo(
    () => buildLedgerSummary(supplierLedgerEntries, selectedSupplierMonth, (entry) => entry?.createdAt),
    [supplierLedgerEntries, selectedSupplierMonth]
  );

  const supplierOverallBalanceRows = useMemo(
    () => buildOverallBalanceRows(materialPurchaseEntries, (entry) => entry?.supplierName),
    [materialPurchaseEntries]
  );

  useEffect(() => {
    if (!isOverallFilter(selectedSupplierName)) {
      setSupplierOverallBreakdown(makeOverallBreakdownState());
    }
  }, [selectedSupplierName, selectedSupplierMonth]);

  useEffect(() => {
    setSupplierSettlementHistoryPage(1);
    setSupplierQtyBreakdownOpen(false);
  }, [selectedSupplierName, selectedSupplierMonth]);

  useEffect(() => {
    const weeks = Array.isArray(supplierSummaryMatrix.weeks) ? supplierSummaryMatrix.weeks : [];
    if (!weeks.length) {
      if (selectedSupplierWeekKey) setSelectedSupplierWeekKey("");
      return;
    }
    if (!weeks.some((week) => week.key === selectedSupplierWeekKey)) {
      setSelectedSupplierWeekKey(weeks[0].key);
    }
  }, [supplierSummaryMatrix.weeks, selectedSupplierWeekKey]);

  const stallCreditEntries = useMemo(
    () =>
      (Array.isArray(salesEntries) ? salesEntries : []).filter(
        (entry) => String(entry?.category || "").trim() === "Billing"
      ),
    [salesEntries]
  );

  const stallCreditCustomerOptions = useMemo(
    () =>
      Array.from(
        new Set(stallCreditEntries.map((entry) => dashboardCustomerLabel(entry?.customer)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [stallCreditEntries]
  );

  const selectedStallOverallValues = useMemo(
    () => resolveOverallSelections(stallCreditCustomerOptions, selectedStallOverallCustomers),
    [stallCreditCustomerOptions, selectedStallOverallCustomers]
  );

  const handleSelectStallCustomerName = (value) => {
    setSelectedStallCustomerName(value);
    setStallOverallBreakdown(makeOverallBreakdownState());
    if (isOverallFilter(value)) {
      setSelectedStallOverallCustomers(null);
    }
  };

  const toggleStallOverallValue = (value) => {
    setSelectedStallOverallCustomers((prev) => toggleSelectionValue(stallCreditCustomerOptions, prev, value));
    setStallOverallBreakdown(makeOverallBreakdownState());
  };

  useEffect(() => {
    if (stallCreditCustomerOptions.length === 0) {
      setSelectedStallCustomerName("");
      setSelectedStallOverallCustomers(null);
      return;
    }
    if (!isOverallFilter(selectedStallCustomerName) && !stallCreditCustomerOptions.includes(selectedStallCustomerName)) {
      setSelectedStallCustomerName(stallCreditCustomerOptions[0] || "");
    }
  }, [stallCreditCustomerOptions, selectedStallCustomerName]);

  useEffect(() => {
    setSelectedStallOverallCustomers((prev) =>
      prev == null ? null : resolveOverallSelections(stallCreditCustomerOptions, prev)
    );
  }, [stallCreditCustomerOptions]);

  const stallCreditCustomerEntries = useMemo(
    () =>
      stallCreditEntries.filter(
        (entry) =>
          isOverallFilter(selectedStallCustomerName)
            ? selectedStallOverallValues.includes(dashboardCustomerLabel(entry?.customer))
            : dashboardCustomerLabel(entry?.customer) === selectedStallCustomerName
      ),
    [stallCreditEntries, selectedStallCustomerName, selectedStallOverallValues]
  );

  const stallCreditEntriesForSummary = useMemo(() => {
    const monthKey = normalizeYm(selectedSupplierMonth);
    if (!selectedStallCustomerName || !monthKey) return [];
    return stallCreditCustomerEntries
      .filter((entry) => {
        const dateKey = normalizeYmd(entry?.saleDate);
        return dateKey && dateKey.startsWith(`${monthKey}-`);
      })
      .sort((a, b) => normalizeYmd(a?.saleDate).localeCompare(normalizeYmd(b?.saleDate)));
  }, [selectedSupplierMonth, selectedStallCustomerName, stallCreditCustomerEntries]);

  const stallCreditSummaryMatrix = useMemo(
    () => buildWeeklyFinanceMatrix(stallCreditEntriesForSummary, selectedSupplierMonth, (entry) => entry?.saleDate),
    [selectedSupplierMonth, stallCreditEntriesForSummary]
  );

  const stallCreditLedgerSummary = useMemo(
    () => buildLedgerSummary(stallCreditCustomerEntries, selectedSupplierMonth, (entry) => entry?.saleDate),
    [selectedSupplierMonth, stallCreditCustomerEntries]
  );

  const stallCreditOverallBalanceRows = useMemo(
    () => buildOverallBalanceRows(stallCreditEntries, (entry) => dashboardCustomerLabel(entry?.customer)),
    [stallCreditEntries]
  );

  useEffect(() => {
    if (!isOverallFilter(selectedStallCustomerName)) {
      setStallOverallBreakdown(makeOverallBreakdownState());
    }
  }, [selectedStallCustomerName, selectedSupplierMonth]);

  useEffect(() => {
    setStallSettlementHistoryPage(1);
  }, [selectedStallCustomerName, selectedSupplierMonth]);

  useEffect(() => {
    const weeks = Array.isArray(stallCreditSummaryMatrix.weeks) ? stallCreditSummaryMatrix.weeks : [];
    if (!weeks.length) {
      if (selectedStallWeekKey) setSelectedStallWeekKey("");
      return;
    }
    if (!weeks.some((week) => week.key === selectedStallWeekKey)) {
      setSelectedStallWeekKey(weeks[0].key);
    }
  }, [stallCreditSummaryMatrix.weeks, selectedStallWeekKey]);

  const marketCreditEntries = useMemo(
    () =>
      (Array.isArray(salesEntries) ? salesEntries : []).filter(
        (entry) => String(entry?.category || "").trim() === "Stall" && !isEveningSalesSource(entry?.source)
      ),
    [salesEntries]
  );

  const marketCreditCustomerOptions = useMemo(
    () =>
      Array.from(
        new Set(marketCreditEntries.map((entry) => dashboardCustomerLabel(entry?.customer)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [marketCreditEntries]
  );

  const selectedMarketOverallValues = useMemo(
    () => resolveOverallSelections(marketCreditCustomerOptions, selectedMarketOverallCustomers),
    [marketCreditCustomerOptions, selectedMarketOverallCustomers]
  );

  const handleSelectMarketCustomerName = (value) => {
    setSelectedMarketCustomerName(value);
    setMarketOverallBreakdown(makeOverallBreakdownState());
    if (isOverallFilter(value)) {
      setSelectedMarketOverallCustomers(null);
    }
  };

  const toggleMarketOverallValue = (value) => {
    setSelectedMarketOverallCustomers((prev) => toggleSelectionValue(marketCreditCustomerOptions, prev, value));
    setMarketOverallBreakdown(makeOverallBreakdownState());
  };

  useEffect(() => {
    if (marketCreditCustomerOptions.length === 0) {
      setSelectedMarketCustomerName("");
      setSelectedMarketOverallCustomers(null);
      return;
    }
    if (!isOverallFilter(selectedMarketCustomerName) && !marketCreditCustomerOptions.includes(selectedMarketCustomerName)) {
      setSelectedMarketCustomerName(marketCreditCustomerOptions[0] || "");
    }
  }, [marketCreditCustomerOptions, selectedMarketCustomerName]);

  useEffect(() => {
    setSelectedMarketOverallCustomers((prev) =>
      prev == null ? null : resolveOverallSelections(marketCreditCustomerOptions, prev)
    );
  }, [marketCreditCustomerOptions]);

  const marketCreditCustomerEntries = useMemo(
    () =>
      marketCreditEntries.filter(
        (entry) =>
          isOverallFilter(selectedMarketCustomerName)
            ? selectedMarketOverallValues.includes(dashboardCustomerLabel(entry?.customer))
            : dashboardCustomerLabel(entry?.customer) === selectedMarketCustomerName
      ),
    [marketCreditEntries, selectedMarketCustomerName, selectedMarketOverallValues]
  );

  const marketCreditEntriesForSummary = useMemo(() => {
    const monthKey = normalizeYm(selectedSupplierMonth);
    if (!selectedMarketCustomerName || !monthKey) return [];
    return marketCreditCustomerEntries
      .filter((entry) => {
        const dateKey = normalizeYmd(entry?.saleDate);
        return dateKey && dateKey.startsWith(`${monthKey}-`);
      })
      .sort((a, b) => normalizeYmd(a?.saleDate).localeCompare(normalizeYmd(b?.saleDate)));
  }, [selectedSupplierMonth, selectedMarketCustomerName, marketCreditCustomerEntries]);

  const marketCreditSummaryMatrix = useMemo(
    () => buildWeeklyFinanceMatrix(marketCreditEntriesForSummary, selectedSupplierMonth, (entry) => entry?.saleDate),
    [selectedSupplierMonth, marketCreditEntriesForSummary]
  );

  const marketCreditLedgerSummary = useMemo(
    () => buildLedgerSummary(marketCreditCustomerEntries, selectedSupplierMonth, (entry) => entry?.saleDate),
    [selectedSupplierMonth, marketCreditCustomerEntries]
  );

  const marketCreditOverallBalanceRows = useMemo(
    () => buildOverallBalanceRows(marketCreditEntries, (entry) => dashboardCustomerLabel(entry?.customer)),
    [marketCreditEntries]
  );

  useEffect(() => {
    if (!isOverallFilter(selectedMarketCustomerName)) {
      setMarketOverallBreakdown(makeOverallBreakdownState());
    }
  }, [selectedMarketCustomerName, selectedSupplierMonth]);

  useEffect(() => {
    setMarketSettlementHistoryPage(1);
  }, [selectedMarketCustomerName, selectedSupplierMonth]);

  useEffect(() => {
    const weeks = Array.isArray(marketCreditSummaryMatrix.weeks) ? marketCreditSummaryMatrix.weeks : [];
    if (!weeks.length) {
      if (selectedMarketWeekKey) setSelectedMarketWeekKey("");
      return;
    }
    if (!weeks.some((week) => week.key === selectedMarketWeekKey)) {
      setSelectedMarketWeekKey(weeks[0].key);
    }
  }, [marketCreditSummaryMatrix.weeks, selectedMarketWeekKey]);

  const isSalesCurrentDayRange = salesRangeStart === currentDateKey && salesRangeEnd === currentDateKey;
  const activeSalesBucketLabel =
    salesBucketFilter === "all" ? "All Sales Streams" : SALES_PULSE_BUCKET_MAP[salesBucketFilter]?.label || "Sales";
  const isSupplierSummaryTab = activeTab === "supplier-summary";
  const isStallCreditSummaryTab = activeTab === "stall-credit-summary";
  const isMarketCreditSummaryTab = activeTab === "market-credit-summary";
  const isMonthSummaryTab =
    isSupplierSummaryTab || isStallCreditSummaryTab || isMarketCreditSummaryTab;
  const salesFiltersDirty =
    salesFromDate !== currentDateKey ||
    salesToDate !== currentDateKey ||
    salesSegmentFilter !== "All" ||
    salesBucketFilter !== "all" ||
    salesPaymentTypeFilter !== "All" ||
    String(salesPartySearch || "").trim() !== "" ||
    String(salesMaterialSearch || "").trim() !== "";

  const buildPendingSettlementEntries = (list, targetEntryIds, getDateValue) =>
    (Array.isArray(list) ? list : [])
      .filter((entry) => targetEntryIds.includes(String(entry?.id || "")))
      .map((entry) => {
        const settlementSummary = getSettlementSummary(Number(entry?.total) || 0, entry?.payments);
        return {
          id: String(entry?.id || ""),
          dateValue: normalizeYmd(getDateValue(entry)),
          balance: settlementSummary.pendingAmount,
        };
      })
      .filter((entry) => entry.id && entry.balance > 0)
      .sort((a, b) => {
        const dateCmp = String(a.dateValue || "").localeCompare(String(b.dateValue || ""));
        if (dateCmp !== 0) return dateCmp;
        return String(a.id || "").localeCompare(String(b.id || ""));
      });

  const openSupplierPaymentModal = ({
    label,
    totalAmount,
    paidAmount,
    discountAmount,
    knockOffAmount,
    pendingAmount,
    targetEntryIds,
  }) => {
    if (!selectedSupplierName) {
      return;
    }
    const safeTargetEntryIds = Array.isArray(targetEntryIds)
      ? Array.from(new Set(targetEntryIds.map((id) => String(id)).filter(Boolean)))
      : [];
    const safePendingAmount = +Number(pendingAmount || 0).toFixed(2);
    const paidDateSummary = buildPaymentDateSummary(inwardEntries, safeTargetEntryIds);
    setSupplierPaymentModal({
      open: true,
      viewOnly: safePendingAmount <= 0 || safeTargetEntryIds.length === 0,
      label: label || "",
      supplierName: selectedSupplierName,
      totalAmount: +Number(totalAmount || 0).toFixed(2),
      paidAmount: +Number(paidAmount || 0).toFixed(2),
      discountAmount: +Number(discountAmount || 0).toFixed(2),
      knockOffAmount: +Number(knockOffAmount || 0).toFixed(2),
      pendingAmount: safePendingAmount,
      paidDateSummary,
      targetEntryIds: safeTargetEntryIds,
    });
    setSupplierPaymentForm(makeSupplierPaymentForm());
  };

  const closeSupplierPaymentModal = (force = false) => {
    if (supplierPaymentSaving && !force) return;
    setSupplierPaymentModal({
      open: false,
      viewOnly: false,
      label: "",
      supplierName: "",
      totalAmount: 0,
      paidAmount: 0,
      discountAmount: 0,
      knockOffAmount: 0,
      pendingAmount: 0,
      paidDateSummary: [],
      targetEntryIds: [],
    });
    setSupplierPaymentForm(makeSupplierPaymentForm());
  };

  const saveSupplierPaymentFromDashboard = async () => {
    if (supplierPaymentSaving) return;
    if (supplierPaymentModal.viewOnly) return;
    const amount = Number(supplierPaymentForm.amount);
    const type = normalizeSettlementType(supplierPaymentForm.type);
    const mode = normalizeDashboardPaymentMode(supplierPaymentForm.mode || "Cash");
    const today = toLocalYmd(new Date());
    const date = String(supplierPaymentForm.date || "").trim() || today;
    const settlementLabel = getSettlementTypeLabel(type);
    if (!amount || amount <= 0) {
      alert(`Enter a valid ${settlementLabel.toLowerCase()} amount.`);
      return;
    }
    if (date > today) {
      alert(`${settlementLabel} date cannot be in the future.`);
      return;
    }

    const relevantEntries = buildPendingSettlementEntries(
      inwardEntries,
      supplierPaymentModal.targetEntryIds,
      (entry) => entry?.createdAt
    );

    const availablePending = +relevantEntries.reduce((sum, entry) => sum + (Number(entry.balance) || 0), 0).toFixed(2);
    if (availablePending <= 0) {
      alert("No pending amount found for this selection.");
      return;
    }
    if (amount > availablePending + 0.0001) {
      alert(`Amount exceeds pending balance of Rs ${formatMoney(availablePending)}.`);
      return;
    }

    let remaining = +amount.toFixed(2);
    setSupplierPaymentSaving(true);
    try {
      for (const entry of relevantEntries) {
        if (remaining <= 0) break;
        const applyAmount = Math.min(remaining, Number(entry.balance) || 0);
        if (applyAmount <= 0) continue;
        await addInwardPayment(entry.id, {
          amount: +applyAmount.toFixed(2),
          date,
          type,
          mode: type === SETTLEMENT_TYPE_PAYMENT ? mode : undefined,
        });
        remaining = +(remaining - applyAmount).toFixed(2);
      }
      await refresh();
      closeSupplierPaymentModal(true);
      alert(`${settlementLabel} saved.`);
    } catch (err) {
      console.error(err);
      alert(err?.message || `Failed to save ${settlementLabel.toLowerCase()}.`);
    } finally {
      setSupplierPaymentSaving(false);
    }
  };

  const revertSupplierKnockOff = async (row) => {
    const paymentId = String(row?.paymentId || "").trim();
    if (!paymentId || normalizeSettlementType(row?.type) !== "knock_off" || supplierRevertingSettlementId) return;
    if (!window.confirm(`Revert knock off of Rs ${formatMoney(row?.amount || 0)} dated ${formatDisplayDate(row?.paymentDate)}?`)) {
      return;
    }
    setSupplierRevertingSettlementId(paymentId);
    try {
      await deleteInwardPayment(paymentId);
      await refresh();
      alert("Knock off reverted.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to revert knock off.");
    } finally {
      setSupplierRevertingSettlementId("");
    }
  };

  const openStallCreditPaymentModal = ({
    label,
    totalAmount,
    paidAmount,
    discountAmount,
    knockOffAmount,
    pendingAmount,
    targetEntryIds,
  }) => {
    if (!selectedStallCustomerName) {
      return;
    }
    const safeTargetEntryIds = Array.isArray(targetEntryIds)
      ? Array.from(new Set(targetEntryIds.map((id) => String(id)).filter(Boolean)))
      : [];
    const safePendingAmount = +Number(pendingAmount || 0).toFixed(2);
    const paidDateSummary = buildPaymentDateSummary(stallCreditEntries, safeTargetEntryIds);
    setStallCreditPaymentModal({
      open: true,
      viewOnly: safePendingAmount <= 0 || safeTargetEntryIds.length === 0,
      label: label || "",
      customerName: selectedStallCustomerName,
      totalAmount: +Number(totalAmount || 0).toFixed(2),
      paidAmount: +Number(paidAmount || 0).toFixed(2),
      discountAmount: +Number(discountAmount || 0).toFixed(2),
      knockOffAmount: +Number(knockOffAmount || 0).toFixed(2),
      pendingAmount: safePendingAmount,
      paidDateSummary,
      targetEntryIds: safeTargetEntryIds,
    });
    setStallCreditPaymentForm(makeSupplierPaymentForm());
  };

  const closeStallCreditPaymentModal = (force = false) => {
    if (stallCreditPaymentSaving && !force) return;
    setStallCreditPaymentModal({
      open: false,
      viewOnly: false,
      label: "",
      customerName: "",
      totalAmount: 0,
      paidAmount: 0,
      discountAmount: 0,
      knockOffAmount: 0,
      pendingAmount: 0,
      paidDateSummary: [],
      targetEntryIds: [],
    });
    setStallCreditPaymentForm(makeSupplierPaymentForm());
  };

  const saveStallCreditPaymentFromDashboard = async () => {
    if (stallCreditPaymentSaving) return;
    if (stallCreditPaymentModal.viewOnly) return;
    const amount = Number(stallCreditPaymentForm.amount);
    const type = normalizeSettlementType(stallCreditPaymentForm.type);
    const mode = normalizeDashboardPaymentMode(stallCreditPaymentForm.mode || "Cash");
    const today = toLocalYmd(new Date());
    const date = String(stallCreditPaymentForm.date || "").trim() || today;
    const settlementLabel = getSettlementTypeLabel(type);
    if (!amount || amount <= 0) {
      alert(`Enter a valid ${settlementLabel.toLowerCase()} amount.`);
      return;
    }
    if (date > today) {
      alert(`${settlementLabel} date cannot be in the future.`);
      return;
    }

    const relevantEntries = buildPendingSettlementEntries(
      stallCreditEntries,
      stallCreditPaymentModal.targetEntryIds,
      (entry) => entry?.saleDate
    );

    const availablePending = +relevantEntries.reduce((sum, entry) => sum + (Number(entry.balance) || 0), 0).toFixed(2);
    if (availablePending <= 0) {
      alert("No pending amount found for this selection.");
      return;
    }
    if (amount > availablePending + 0.0001) {
      alert(`Amount exceeds pending balance of Rs ${formatMoney(availablePending)}.`);
      return;
    }

    let remaining = +amount.toFixed(2);
    setStallCreditPaymentSaving(true);
    try {
      for (const entry of relevantEntries) {
        if (remaining <= 0) break;
        const applyAmount = Math.min(remaining, Number(entry.balance) || 0);
        if (applyAmount <= 0) continue;
        await addSalesPayment(entry.id, {
          amount: +applyAmount.toFixed(2),
          date,
          type,
          mode: type === SETTLEMENT_TYPE_PAYMENT ? mode : undefined,
        });
        remaining = +(remaining - applyAmount).toFixed(2);
      }
      await refresh();
      closeStallCreditPaymentModal(true);
      alert(`${settlementLabel} saved.`);
    } catch (err) {
      console.error(err);
      alert(err?.message || `Failed to save ${settlementLabel.toLowerCase()}.`);
    } finally {
      setStallCreditPaymentSaving(false);
    }
  };

  const revertStallKnockOff = async (row) => {
    const paymentId = String(row?.paymentId || "").trim();
    if (!paymentId || normalizeSettlementType(row?.type) !== "knock_off" || stallRevertingSettlementId) return;
    if (!window.confirm(`Revert knock off of Rs ${formatMoney(row?.amount || 0)} dated ${formatDisplayDate(row?.paymentDate)}?`)) {
      return;
    }
    setStallRevertingSettlementId(paymentId);
    try {
      await deleteSalesPayment(paymentId);
      await refresh();
      alert("Knock off reverted.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to revert knock off.");
    } finally {
      setStallRevertingSettlementId("");
    }
  };

  const openMarketCreditPaymentModal = ({
    label,
    totalAmount,
    paidAmount,
    discountAmount,
    knockOffAmount,
    pendingAmount,
    targetEntryIds,
  }) => {
    if (!selectedMarketCustomerName) {
      return;
    }
    const safeTargetEntryIds = Array.isArray(targetEntryIds)
      ? Array.from(new Set(targetEntryIds.map((id) => String(id)).filter(Boolean)))
      : [];
    const safePendingAmount = +Number(pendingAmount || 0).toFixed(2);
    const paidDateSummary = buildPaymentDateSummary(marketCreditEntries, safeTargetEntryIds);
    setMarketCreditPaymentModal({
      open: true,
      viewOnly: safePendingAmount <= 0 || safeTargetEntryIds.length === 0,
      label: label || "",
      customerName: selectedMarketCustomerName,
      totalAmount: +Number(totalAmount || 0).toFixed(2),
      paidAmount: +Number(paidAmount || 0).toFixed(2),
      discountAmount: +Number(discountAmount || 0).toFixed(2),
      knockOffAmount: +Number(knockOffAmount || 0).toFixed(2),
      pendingAmount: safePendingAmount,
      paidDateSummary,
      targetEntryIds: safeTargetEntryIds,
    });
    setMarketCreditPaymentForm(makeSupplierPaymentForm());
  };

  const closeMarketCreditPaymentModal = (force = false) => {
    if (marketCreditPaymentSaving && !force) return;
    setMarketCreditPaymentModal({
      open: false,
      viewOnly: false,
      label: "",
      customerName: "",
      totalAmount: 0,
      paidAmount: 0,
      discountAmount: 0,
      knockOffAmount: 0,
      pendingAmount: 0,
      paidDateSummary: [],
      targetEntryIds: [],
    });
    setMarketCreditPaymentForm(makeSupplierPaymentForm());
  };

  const saveMarketCreditPaymentFromDashboard = async () => {
    if (marketCreditPaymentSaving) return;
    if (marketCreditPaymentModal.viewOnly) return;
    const amount = Number(marketCreditPaymentForm.amount);
    const type = normalizeSettlementType(marketCreditPaymentForm.type);
    const mode = normalizeDashboardPaymentMode(marketCreditPaymentForm.mode || "Cash");
    const today = toLocalYmd(new Date());
    const date = String(marketCreditPaymentForm.date || "").trim() || today;
    const settlementLabel = getSettlementTypeLabel(type);
    if (!amount || amount <= 0) {
      alert(`Enter a valid ${settlementLabel.toLowerCase()} amount.`);
      return;
    }
    if (date > today) {
      alert(`${settlementLabel} date cannot be in the future.`);
      return;
    }

    const relevantEntries = buildPendingSettlementEntries(
      marketCreditEntries,
      marketCreditPaymentModal.targetEntryIds,
      (entry) => entry?.saleDate
    );

    const availablePending = +relevantEntries.reduce((sum, entry) => sum + (Number(entry.balance) || 0), 0).toFixed(2);
    if (availablePending <= 0) {
      alert("No pending amount found for this selection.");
      return;
    }
    if (amount > availablePending + 0.0001) {
      alert(`Amount exceeds pending balance of Rs ${formatMoney(availablePending)}.`);
      return;
    }

    let remaining = +amount.toFixed(2);
    setMarketCreditPaymentSaving(true);
    try {
      for (const entry of relevantEntries) {
        if (remaining <= 0) break;
        const applyAmount = Math.min(remaining, Number(entry.balance) || 0);
        if (applyAmount <= 0) continue;
        await addSalesPayment(entry.id, {
          amount: +applyAmount.toFixed(2),
          date,
          type,
          mode: type === SETTLEMENT_TYPE_PAYMENT ? mode : undefined,
        });
        remaining = +(remaining - applyAmount).toFixed(2);
      }
      await refresh();
      closeMarketCreditPaymentModal(true);
      alert(`${settlementLabel} saved.`);
    } catch (err) {
      console.error(err);
      alert(err?.message || `Failed to save ${settlementLabel.toLowerCase()}.`);
    } finally {
      setMarketCreditPaymentSaving(false);
    }
  };

  const revertMarketKnockOff = async (row) => {
    const paymentId = String(row?.paymentId || "").trim();
    if (!paymentId || normalizeSettlementType(row?.type) !== "knock_off" || marketRevertingSettlementId) return;
    if (!window.confirm(`Revert knock off of Rs ${formatMoney(row?.amount || 0)} dated ${formatDisplayDate(row?.paymentDate)}?`)) {
      return;
    }
    setMarketRevertingSettlementId(paymentId);
    try {
      await deleteSalesPayment(paymentId);
      await refresh();
      alert("Knock off reverted.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to revert knock off.");
    } finally {
      setMarketRevertingSettlementId("");
    }
  };

  const openDashboardPicker = () => {
    setMonthPickerYear(Number(selectedSupplierMonth.slice(0, 4)) || Number(currentMonthKey.slice(0, 4)));
    setMonthPickerOpen((open) => !open);
  };

  const resetSalesPulseFilters = () => {
    setSalesFromDate(currentDateKey);
    setSalesToDate(currentDateKey);
    setSalesSegmentFilter("All");
    setSalesBucketFilter("all");
    setSalesPaymentTypeFilter("All");
    setSalesPartySearch("");
    setSalesMaterialSearch("");
  };

  const openOverallBalanceModal = ({ title, entityLabel, amountLabel, rows }) => {
    setOverallBalanceModal({
      open: true,
      title: title || "Overall Balance",
      entityLabel: entityLabel || "Name",
      amountLabel: amountLabel || "Total Amount",
      rows: Array.isArray(rows) ? rows : [],
    });
    setOverallBalancePage(1);
    setOverallBalanceFilter("");
    setOverallBalanceVisibleColumns(makeOverallBalanceVisibleColumns());
  };

  const closeOverallBalanceModal = () => {
    setOverallBalanceModal(makeOverallBalanceModalState());
    setOverallBalancePage(1);
    setOverallBalanceFilter("");
    setOverallBalanceVisibleColumns(makeOverallBalanceVisibleColumns());
  };

  const renderFinancialSummaryPanel = ({
    filterLabel,
    overallFilterLabel,
    emptyOptionLabel,
    selectedName,
    onSelectName,
    overallSelectedValues,
    onToggleOverallValue,
    onSelectAllOverall,
    selectedWeekKey,
    onSelectWeek,
    options,
    summaryMatrix,
    summaryEntries,
    ledgerSummary,
    totalLabel,
    quantityLabel,
    quantityValue,
    onOpenQuantityBreakdown,
    ledgerAmountLabel,
    openPaymentModal,
    emptyMessage,
    getEntryDate,
    getBreakdownLabel,
    overallBreakdown,
    onOpenOverallBreakdown,
    onCloseOverallBreakdown,
    historyPage,
    onChangeHistoryPage,
    revertingHistoryPaymentId,
    onRevertHistoryRow,
    onOpenOverallBalance,
    overallBalanceDisabled,
  }) => {
    const isOverallSelected = isOverallFilter(selectedName);
    const selectionOptions = options.length
      ? [{ value: OVERALL_FILTER_VALUE, label: "Overall" }, ...options.map((name) => ({ value: name, label: name }))]
      : [];
    const weekOptions = (summaryMatrix?.weeks || []).map((week, index) => ({
      key: week.key,
      label: `Week ${index + 1} ${formatDisplayDate(week.start)} to ${formatDisplayDate(week.end)}`,
      start: week.start,
      end: week.end,
    }));
    const activeWeekOption =
      weekOptions.find((week) => week.key === selectedWeekKey) ||
      weekOptions[0] ||
      null;
    const weekRows = activeWeekOption
      ? (ledgerSummary?.balanceRows || []).filter(
          (row) => row.date >= activeWeekOption.start && row.date <= activeWeekOption.end
        )
      : [];
    const priorRows = activeWeekOption
      ? (ledgerSummary?.balanceRows || []).filter((row) => row.date < activeWeekOption.start)
      : [];
    const weekOpeningBalance = activeWeekOption
      ? priorRows.length
        ? Number(priorRows[priorRows.length - 1]?.balanceAmount || 0)
        : Number(ledgerSummary?.openingBalance || 0)
      : Number(ledgerSummary?.openingBalance || 0);
    const weekClosingBalance = activeWeekOption
      ? weekRows.length
        ? Number(weekRows[weekRows.length - 1]?.balanceAmount || 0)
        : weekOpeningBalance
      : Number(ledgerSummary?.closingBalance || 0);
    const weekPurchaseTotal = weekRows.reduce((sum, row) => sum + (Number(row?.purchaseAmount) || 0), 0);
    const weekPaidTotal = weekRows.reduce((sum, row) => sum + (Number(row?.paidAmount) || 0), 0);
    const weekKnockOffTotal = weekRows.reduce((sum, row) => sum + (Number(row?.knockOffAmount) || 0), 0);
    const breakdownRows = Array.isArray(overallBreakdown?.rows) ? overallBreakdown.rows : [];
    const historyRows = Array.isArray(ledgerSummary?.historyRows) ? ledgerSummary.historyRows : [];
    const historyTotalPages = Math.max(1, Math.ceil(historyRows.length / SETTLEMENT_HISTORY_PAGE_SIZE));
    const safeHistoryPage = Math.min(Math.max(Number(historyPage) || 1, 1), historyTotalPages);
    const historyPageStartIndex = (safeHistoryPage - 1) * SETTLEMENT_HISTORY_PAGE_SIZE;
    const pagedHistoryRows = historyRows.slice(
      historyPageStartIndex,
      historyPageStartIndex + SETTLEMENT_HISTORY_PAGE_SIZE
    );
    const historyStart = historyRows.length === 0 ? 0 : historyPageStartIndex + 1;
    const historyEnd =
      historyRows.length === 0 ? 0 : Math.min(historyPageStartIndex + SETTLEMENT_HISTORY_PAGE_SIZE, historyRows.length);

    const openBreakdownForEntries = (label, entries) => {
      if (!isOverallSelected || typeof onOpenOverallBreakdown !== "function") return;
      onOpenOverallBreakdown({
        label,
        rows: buildOverallBreakdownRows(entries, getBreakdownLabel),
      });
    };

    const getEntriesForDate = (date) =>
      (Array.isArray(summaryEntries) ? summaryEntries : []).filter(
        (entry) => normalizeYmd(getEntryDate(entry)) === normalizeYmd(date)
      );

    const getEntriesForRange = (start, end) =>
      (Array.isArray(summaryEntries) ? summaryEntries : []).filter((entry) => {
        const dateKey = normalizeYmd(getEntryDate(entry));
        return dateKey && dateKey >= start && dateKey <= end;
      });

    return (
    <div className="supplier-summary-panel">
      <div className="supplier-summary-toolbar">
        <div className="supplier-summary-controls">
          <label className="supplier-summary-filter">
            <span>{filterLabel}</span>
            <select
              value={selectedName}
              onChange={(event) => onSelectName(event.target.value)}
              className="select-field"
            >
              {selectionOptions.length === 0 ? <option value="">{emptyOptionLabel}</option> : null}
              {selectionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {isOverallSelected ? (
            <SummaryChecklistDropdown
              label={overallFilterLabel}
              options={options}
              selectedValues={overallSelectedValues}
              onToggleValue={onToggleOverallValue}
              onSelectAll={onSelectAllOverall}
            />
          ) : null}
          <button
            type="button"
            className="btn-secondary supplier-summary-overall-balance-btn"
            onClick={onOpenOverallBalance}
            disabled={overallBalanceDisabled}
          >
            Overall Balance
          </button>
        </div>

        <div className="supplier-summary-stats">
          <div className="supplier-summary-stat">
            <span>{totalLabel}</span>
            <strong>{`Rs ${formatMoney(summaryMatrix.totalAmount)}`}</strong>
            {quantityLabel ? (
              typeof onOpenQuantityBreakdown === "function" ? (
                <button
                  type="button"
                  className="supplier-summary-stat-link"
                  onClick={onOpenQuantityBreakdown}
                  disabled={Number(quantityValue || 0) <= 0}
                >
                  {`${quantityLabel}: ${Number(quantityValue || 0).toFixed(2)}`}
                </button>
              ) : (
                <small>{`${quantityLabel}: ${Number(quantityValue || 0).toFixed(2)}`}</small>
              )
            ) : null}
          </div>
          <div className="supplier-summary-stat">
            <span>Total Paid</span>
            <strong>{`Rs ${formatMoney(summaryMatrix.totalPaidAmount)}`}</strong>
          </div>
          <div className="supplier-summary-stat">
            <span>Total Knock Off</span>
            <strong>{`Rs ${formatMoney(summaryMatrix.totalKnockOffAmount)}`}</strong>
          </div>
          <div className="supplier-summary-stat">
            <span>Total Balance</span>
            <strong>{`Rs ${formatMoney(summaryMatrix.totalPendingAmount)}`}</strong>
          </div>
          <div className="supplier-summary-stat">
            <span>Entries Count</span>
            <strong>{summaryMatrix.totalEntries}</strong>
          </div>
          <div className="supplier-summary-stat">
            <span>Weeks Visible</span>
            <strong>{summaryMatrix.weeks.length}</strong>
          </div>
        </div>
      </div>

      {selectedName ? (
        <>
          <div className="supplier-summary-table-wrap">
            <table className="supplier-summary-table">
              <thead>
                <tr>
                  <th>Week</th>
                  {SUPPLIER_WEEK_DAYS.map((day) => (
                    <th key={day.key}>{day.label}</th>
                  ))}
                  <th>Week Total</th>
                  <th>Week Paid</th>
                  <th>Week Balance</th>
                </tr>
              </thead>
              <tbody>
                {summaryMatrix.weeks.map((week, index) => (
                  <tr key={week.key}>
                    <th>
                      <div className="supplier-summary-week-label">
                        {`Week ${index + 1} ${formatDisplayDate(week.start)} to ${formatDisplayDate(week.end)}`}
                      </div>
                    </th>
                    {SUPPLIER_WEEK_DAYS.map((day) => {
                      const cell = week.values[day.key];
                      const amount = cell?.amount ?? null;
                      const pendingAmount = Number(cell?.pendingAmount || 0);
                      const displayAmount = amount == null ? null : pendingAmount;
                      const cellModalPayload = {
                        label: `${day.label} ${formatDisplayDate(cell?.date)}`,
                        totalAmount: amount,
                        paidAmount: cell?.paidAmount || 0,
                        discountAmount: cell?.discountAmount || 0,
                        knockOffAmount: cell?.knockOffAmount || 0,
                        pendingAmount,
                        targetEntryIds: cell?.targetEntryIds || [],
                      };
                      return (
                        <td
                          key={`${week.key}-${day.key}`}
                          className={displayAmount == null ? "is-na" : "is-value"}
                        >
                          {displayAmount == null ? (
                            "N/A"
                          ) : (
                            <div className="supplier-summary-cell">
                              {isOverallSelected ? (
                                <button
                                  type="button"
                                  className="supplier-summary-value-btn"
                                  onClick={() =>
                                    openBreakdownForEntries(
                                      `${day.label} ${formatDisplayDate(cell.date)}`,
                                      getEntriesForDate(cell.date)
                                    )
                                  }
                                >
                                  {`Rs ${formatMoney(displayAmount)}`}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="supplier-summary-value-btn"
                                  onClick={() => openPaymentModal(cellModalPayload)}
                                >
                                  {`Rs ${formatMoney(displayAmount)}`}
                                </button>
                              )}
                              {!isOverallSelected && pendingAmount > 0 ? (
                                <button
                                  type="button"
                                  className="supplier-summary-pay-btn"
                                  onClick={() => openPaymentModal(cellModalPayload)}
                                  title={`Update pending Rs ${formatMoney(pendingAmount)}`}
                                  aria-label={`Update pending Rs ${formatMoney(pendingAmount)} for ${day.label}`}
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 5v14" />
                                    <path d="M17 9.5c0-1.93-2.24-3.5-5-3.5s-5 1.57-5 3.5 2.24 3.5 5 3.5 5 1.57 5 3.5-2.24 3.5-5 3.5-5-1.57-5-3.5" />
                                  </svg>
                                </button>
                              ) : null}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="is-total">
                      <div className="supplier-summary-cell">
                        {isOverallSelected ? (
                          <button
                            type="button"
                            className="supplier-summary-value-btn"
                            onClick={() =>
                              openBreakdownForEntries(
                                `Week ${index + 1} ${formatDisplayDate(week.start)} to ${formatDisplayDate(week.end)}`,
                                getEntriesForRange(week.start, week.end)
                              )
                            }
                          >
                            {`Rs ${formatMoney(week.total)}`}
                          </button>
                        ) : (
                          <span>{`Rs ${formatMoney(week.total)}`}</span>
                        )}
                      </div>
                    </td>
                    <td className="is-paid-total">
                      <div className="supplier-summary-cell">
                        {isOverallSelected ? (
                          <button
                            type="button"
                            className="supplier-summary-value-btn"
                            onClick={() =>
                              openBreakdownForEntries(
                                `Week Paid ${index + 1} ${formatDisplayDate(week.start)} to ${formatDisplayDate(week.end)}`,
                                getEntriesForRange(week.start, week.end)
                              )
                            }
                          >
                            {`Rs ${formatMoney(week.paidTotal)}`}
                          </button>
                        ) : (
                          <span>{`Rs ${formatMoney(week.paidTotal)}`}</span>
                        )}
                      </div>
                    </td>
                    <td className="is-balance-total">
                      <div className="supplier-summary-cell">
                        {(() => {
                          const weekModalPayload = {
                            label: `Week ${index + 1} ${formatDisplayDate(week.start)} to ${formatDisplayDate(week.end)}`,
                            totalAmount: week.total,
                            paidAmount: week.paidTotal,
                            discountAmount: week.discountTotal,
                            knockOffAmount: week.knockOffTotal,
                            pendingAmount: week.pendingTotal,
                            targetEntryIds: week.targetEntryIds || [],
                          };
                          return isOverallSelected ? (
                            <button
                              type="button"
                              className="supplier-summary-value-btn"
                              onClick={() =>
                                openBreakdownForEntries(
                                  `Week Balance ${index + 1} ${formatDisplayDate(week.start)} to ${formatDisplayDate(week.end)}`,
                                  getEntriesForRange(week.start, week.end)
                                )
                              }
                            >
                              {`Rs ${formatMoney(week.pendingTotal)}`}
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="supplier-summary-value-btn"
                                onClick={() => openPaymentModal(weekModalPayload)}
                              >
                                {`Rs ${formatMoney(week.pendingTotal)}`}
                              </button>
                              {Number(week.pendingTotal || 0) > 0 ? (
                                <button
                                  type="button"
                                  className="supplier-summary-pay-btn"
                                  onClick={() => openPaymentModal(weekModalPayload)}
                                  title={`Update pending Rs ${formatMoney(week.pendingTotal)}`}
                                  aria-label={`Update pending Rs ${formatMoney(week.pendingTotal)} for week ${index + 1}`}
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 5v14" />
                                    <path d="M17 9.5c0-1.93-2.24-3.5-5-3.5s-5 1.57-5 3.5 2.24 3.5 5 3.5 5 1.57 5 3.5-2.24 3.5-5 3.5-5-1.57-5-3.5" />
                                  </svg>
                                </button>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>Month Total</th>
                  {SUPPLIER_WEEK_DAYS.map((day) => (
                    <td key={`month-total-${day.key}`} className="is-month-total">
                      <div className="supplier-summary-cell">
                        <button
                          type="button"
                          className="supplier-summary-value-btn"
                          onClick={() =>
                            openPaymentModal({
                              label: `${day.label} total for ${formatMonthLabel(selectedSupplierMonth)}`,
                              totalAmount: summaryMatrix.dayTotals?.[day.key] || 0,
                              paidAmount: summaryMatrix.dayPaidTotals?.[day.key] || 0,
                              discountAmount: summaryMatrix.dayDiscountTotals?.[day.key] || 0,
                              knockOffAmount: summaryMatrix.dayKnockOffTotals?.[day.key] || 0,
                              pendingAmount: summaryMatrix.dayPendingTotals?.[day.key] || 0,
                              targetEntryIds: summaryMatrix.dayTargetEntryIds?.[day.key] || [],
                            })
                          }
                          disabled={isOverallSelected}
                        >
                          {`Rs ${formatMoney(summaryMatrix.dayPendingTotals?.[day.key] || 0)}`}
                        </button>
                        {!isOverallSelected && Number(summaryMatrix.dayPendingTotals?.[day.key] || 0) > 0 ? (
                          <button
                            type="button"
                            className="supplier-summary-pay-btn"
                            onClick={() =>
                              openPaymentModal({
                                label: `${day.label} total for ${formatMonthLabel(selectedSupplierMonth)}`,
                                totalAmount: summaryMatrix.dayTotals?.[day.key] || 0,
                                paidAmount: summaryMatrix.dayPaidTotals?.[day.key] || 0,
                                discountAmount: summaryMatrix.dayDiscountTotals?.[day.key] || 0,
                                knockOffAmount: summaryMatrix.dayKnockOffTotals?.[day.key] || 0,
                                pendingAmount: summaryMatrix.dayPendingTotals?.[day.key] || 0,
                                targetEntryIds: summaryMatrix.dayTargetEntryIds?.[day.key] || [],
                              })
                            }
                            title={`Update pending Rs ${formatMoney(summaryMatrix.dayPendingTotals?.[day.key] || 0)}`}
                            aria-label={`Update pending Rs ${formatMoney(summaryMatrix.dayPendingTotals?.[day.key] || 0)} for ${day.label}`}
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M12 5v14" />
                              <path d="M17 9.5c0-1.93-2.24-3.5-5-3.5s-5 1.57-5 3.5 2.24 3.5 5 3.5 5 1.57 5 3.5-2.24 3.5-5 3.5-5-1.57-5-3.5" />
                            </svg>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ))}
                  <td className="is-month-total-purchase">
                    <div className="supplier-summary-cell">
                      <span>{`Rs ${formatMoney(summaryMatrix.totalAmount)}`}</span>
                    </div>
                  </td>
                  <td className="is-month-total-paid">
                    <div className="supplier-summary-cell">
                      <span>{`Rs ${formatMoney(summaryMatrix.totalPaidAmount)}`}</span>
                    </div>
                  </td>
                  <td className="is-month-grand-total">
                    <div className="supplier-summary-cell">
                      <button
                        type="button"
                        className="supplier-summary-value-btn"
                        onClick={() =>
                          openPaymentModal({
                            label: `Month total ${formatMonthLabel(selectedSupplierMonth)}`,
                            totalAmount: summaryMatrix.totalAmount,
                            paidAmount: summaryMatrix.totalPaidAmount,
                            discountAmount: summaryMatrix.totalDiscountAmount,
                            knockOffAmount: summaryMatrix.totalKnockOffAmount,
                            pendingAmount: summaryMatrix.totalPendingAmount,
                            targetEntryIds: summaryMatrix.monthTargetEntryIds || [],
                          })
                        }
                        disabled={isOverallSelected}
                      >
                        {`Rs ${formatMoney(summaryMatrix.totalPendingAmount)}`}
                      </button>
                      {!isOverallSelected && Number(summaryMatrix.totalPendingAmount || 0) > 0 ? (
                        <button
                          type="button"
                          className="supplier-summary-pay-btn"
                          onClick={() =>
                            openPaymentModal({
                              label: `Month total ${formatMonthLabel(selectedSupplierMonth)}`,
                              totalAmount: summaryMatrix.totalAmount,
                              paidAmount: summaryMatrix.totalPaidAmount,
                              discountAmount: summaryMatrix.totalDiscountAmount,
                              knockOffAmount: summaryMatrix.totalKnockOffAmount,
                              pendingAmount: summaryMatrix.totalPendingAmount,
                              targetEntryIds: summaryMatrix.monthTargetEntryIds || [],
                            })
                          }
                          title={`Update pending Rs ${formatMoney(summaryMatrix.totalPendingAmount)}`}
                          aria-label={`Update pending Rs ${formatMoney(summaryMatrix.totalPendingAmount)} for month total`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5v14" />
                            <path d="M17 9.5c0-1.93-2.24-3.5-5-3.5s-5 1.57-5 3.5 2.24 3.5 5 3.5 5 1.57 5 3.5-2.24 3.5-5 3.5-5-1.57-5-3.5" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {isOverallSelected ? (
            <div className="dashboard-table-panel supplier-breakdown-panel">
              <div className="dashboard-table-panel__head">
                <div className="dashboard-table-panel__title-wrap">
                  <h3>Overall Breakup</h3>
                  <span className="dashboard-table-panel__meta">
                    {overallBreakdown?.label
                      ? overallBreakdown.label
                      : `Click any weekly value to view ${filterLabel.toLowerCase()} breakup`}
                  </span>
                </div>
                {breakdownRows.length > 0 ? (
                  <button type="button" className="btn-secondary" onClick={onCloseOverallBreakdown}>
                    Clear
                  </button>
                ) : null}
              </div>
              <div className="dashboard-table-scroll">
                <table className="dashboard-sales-table supplier-ledger-table">
                  <thead>
                    <tr>
                      <th>{filterLabel}</th>
                      <th className="text-right">Entries</th>
                      <th className="text-right">{totalLabel}</th>
                      <th className="text-right">Paid</th>
                      <th className="text-right">Knock Off</th>
                      <th className="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdownRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-state" style={{ padding: 16 }}>
                          Click any value in the weekly table to see the segregated breakup.
                        </td>
                      </tr>
                    ) : (
                      breakdownRows.map((row) => (
                        <tr key={`${row.label}-${row.entries}`}>
                          <td>{row.label}</td>
                          <td className="text-right">{row.entries}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.totalAmount)}`}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.paidAmount)}`}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.knockOffAmount)}`}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.balanceAmount)}`}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          <div className="supplier-ledger-grid">
            <div className="dashboard-table-panel">
              <div className="dashboard-table-panel__head">
                <div className="dashboard-table-panel__title-wrap">
                  <h3>Week-wise Payment Balance</h3>
                  <span className="dashboard-table-panel__meta">
                    {`Opening Rs ${formatMoney(weekOpeningBalance)} | Closing Rs ${formatMoney(weekClosingBalance)}`}
                  </span>
                </div>
                <label className="dashboard-table-panel__filter">
                  <span>Select Week</span>
                  <select
                    className="select-field"
                    value={activeWeekOption?.key || ""}
                    onChange={(event) => onSelectWeek(event.target.value)}
                    disabled={!weekOptions.length}
                  >
                    {weekOptions.length === 0 ? <option value="">No Weeks</option> : null}
                    {weekOptions.map((week) => (
                      <option key={week.key} value={week.key}>
                        {week.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="dashboard-table-scroll">
                <table className="dashboard-sales-table supplier-ledger-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-right">{ledgerAmountLabel}</th>
                      <th className="text-right">Paid</th>
                      <th className="text-right">Knock Off</th>
                      <th className="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="empty-state" style={{ padding: 16 }}>
                          {activeWeekOption
                            ? `No balance activity found for ${activeWeekOption.label}`
                            : `No balance activity found for ${formatMonthLabel(selectedSupplierMonth)}`}
                        </td>
                      </tr>
                    ) : (
                      weekRows.map((row) => (
                        <tr key={row.date}>
                          <td>{formatDisplayDate(row.date)}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.purchaseAmount)}`}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.paidAmount)}`}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.knockOffAmount)}`}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.balanceAmount)}`}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Week Total</th>
                      <td className="text-right">{`Rs ${formatMoney(weekPurchaseTotal)}`}</td>
                      <td className="text-right">{`Rs ${formatMoney(weekPaidTotal)}`}</td>
                      <td className="text-right">{`Rs ${formatMoney(weekKnockOffTotal)}`}</td>
                      <td className="text-right">{`Rs ${formatMoney(weekClosingBalance)}`}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="dashboard-table-panel">
              <div className="dashboard-table-panel__head">
                <h3>Settlement History</h3>
                <div>{`${historyRows.length} entries in ${formatMonthLabel(
                  selectedSupplierMonth
                )}`}</div>
              </div>
              <div className="dashboard-table-scroll">
                <table className="dashboard-sales-table supplier-ledger-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Against Date</th>
                      <th>Type</th>
                      <th>Mode</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-state" style={{ padding: 16 }}>
                          No settlements recorded for {formatMonthLabel(selectedSupplierMonth)}
                        </td>
                      </tr>
                    ) : (
                      pagedHistoryRows.map((row) => (
                        <tr key={row.paymentId || `${row.paymentDate}-${row.againstDate}-${row.amount}`}>
                          <td>{formatDisplayDate(row.paymentDate)}</td>
                          <td>{row.againstDate ? formatDisplayDate(row.againstDate) : "-"}</td>
                          <td>{getSettlementTypeLabel(row.type)}</td>
                          <td>{row.mode || "-"}</td>
                          <td className="text-right">{`Rs ${formatMoney(row.amount)}`}</td>
                          <td className="text-right">
                            {normalizeSettlementType(row.type) === "knock_off" &&
                            typeof onRevertHistoryRow === "function" ? (
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => onRevertHistoryRow(row)}
                                disabled={revertingHistoryPaymentId === row.paymentId}
                                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                              >
                                {revertingHistoryPaymentId === row.paymentId ? "Reverting..." : "Revert"}
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {historyRows.length > 0 ? (
                <div
                  style={{
                    paddingTop: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontSize: "0.82rem", color: "#94a3b8" }}>
                    {`Showing ${historyStart}-${historyEnd} of ${historyRows.length} settlements`}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => onChangeHistoryPage(Math.max(1, safeHistoryPage - 1))}
                      disabled={safeHistoryPage <= 1}
                    >
                      Prev
                    </button>
                    <span className="entry-count">{`Page ${safeHistoryPage} / ${historyTotalPages}`}</span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => onChangeHistoryPage(Math.min(historyTotalPages, safeHistoryPage + 1))}
                      disabled={safeHistoryPage >= historyTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <div className="dashboard-breakdown-panel supplier-summary-empty">
          {emptyMessage}
        </div>
      )}
    </div>
  );
  };

  const renderDashboardPaymentModal = ({
    open,
    viewOnly,
    title,
    nameLabel,
    nameValue,
    label,
    totalAmount,
    paidAmount,
    paidDateSummary,
    knockOffAmount,
    pendingAmount,
    form,
    onChangeForm,
    saving,
    onClose,
    onSave,
  }) => {
    if (!open) return null;
    const currentType = normalizeSettlementType(form?.type);
    const currentTypeLabel = getSettlementTypeLabel(currentType);
    const currentMode = normalizeDashboardPaymentMode(form?.mode || "Cash");
    return (
      <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => onClose()}>
        <div className="modal-card" style={{ maxWidth: "720px" }} onClick={(event) => event.stopPropagation()}>
          <div
            className="card-header"
            style={{ justifyContent: "space-between", alignItems: "center", gap: 12 }}
          >
            <h3 className="modal-title" style={{ margin: 0 }}>
              {title}
            </h3>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => onClose()}
              disabled={saving}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem" }}
            >
              Close
            </button>
          </div>
          <div className="inward-form">
            <div className="supplier-payment-meta">
              <div className="supplier-payment-meta-item">
                <span>{nameLabel}</span>
                <strong>{nameValue || "-"}</strong>
              </div>
              <div className="supplier-payment-meta-item">
                <span>Selection</span>
                <strong>{label || "-"}</strong>
              </div>
              <div className="supplier-payment-meta-item">
                <span>Total Amount</span>
                <strong>{`Rs ${formatMoney(totalAmount)}`}</strong>
              </div>
              <div className="supplier-payment-meta-item">
                <span>Paid Amount</span>
                <strong>{`Rs ${formatMoney(paidAmount)}`}</strong>
              </div>
              <div className="supplier-payment-meta-item supplier-payment-meta-item--wide">
                <span>Paid Dates</span>
                {Array.isArray(paidDateSummary) && paidDateSummary.length > 0 ? (
                  <div className="supplier-payment-date-list">
                    {paidDateSummary.map((row) => (
                      <div key={row.date} className="supplier-payment-date-row">
                        <div className="supplier-payment-date-copy">
                          <strong>{formatDisplayDate(row.date)}</strong>
                          {row.modesText ? (
                            <small className="supplier-payment-date-mode">{row.modesText}</small>
                          ) : null}
                        </div>
                        <small>{`Rs ${formatMoney(row.amount)}`}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <strong>-</strong>
                )}
              </div>
              <div className="supplier-payment-meta-item">
                <span>Knock Off Amount</span>
                <strong>{`Rs ${formatMoney(knockOffAmount)}`}</strong>
              </div>
              <div className="supplier-payment-meta-item">
                <span>Pending Amount</span>
                <strong>{`Rs ${formatMoney(pendingAmount)}`}</strong>
              </div>
            </div>
            {viewOnly ? (
              <div className="dashboard-payment-view-note">
                No pending balance is available for this selection. This window is view-only.
              </div>
            ) : (
              <>
                <div
                  className="form-grid"
                  style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
                >
                  <div className="field">
                    <label>Entry Type</label>
                    <select
                      className="input-field"
                      value={currentType}
                      onChange={(event) =>
                        onChangeForm((prev) => ({ ...prev, type: normalizeSettlementType(event.target.value) }))
                      }
                    >
                      <option value="payment">Payment</option>
                      <option value="knock_off">Knock Off</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Entry Date</label>
                    <input
                      className="input-field"
                      type="date"
                      value={form.date}
                      onChange={(event) =>
                        onChangeForm((prev) => ({ ...prev, date: event.target.value }))
                      }
                      max={toLocalYmd(new Date())}
                    />
                  </div>
                  {currentType === SETTLEMENT_TYPE_PAYMENT ? (
                    <div className="field">
                      <label>Payment Type</label>
                      <select
                        className="input-field"
                        value={currentMode}
                        onChange={(event) =>
                          onChangeForm((prev) => ({ ...prev, mode: normalizeDashboardPaymentMode(event.target.value) }))
                        }
                      >
                        {DASHBOARD_PAYMENT_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  <div className="field">
                    <label>{`${currentTypeLabel} Amount`}</label>
                    <input
                      className="input-field"
                      type="number"
                      step="0.01"
                      min="0"
                      max={pendingAmount || undefined}
                      value={form.amount}
                      onChange={(event) =>
                        onChangeForm((prev) => ({ ...prev, amount: event.target.value }))
                      }
                      placeholder={`Max Rs ${formatMoney(pendingAmount)}`}
                    />
                  </div>
                </div>

                <div className="form-actions" style={{ justifyContent: "flex-end" }}>
                  <button type="button" className="btn-secondary" onClick={() => onClose()} disabled={saving}>
                    Cancel
                  </button>
                  <button type="button" className="btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : `Save ${currentTypeLabel}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOverallBalanceModal = () => {
    if (!overallBalanceModal.open) return null;

    const rows = Array.isArray(overallBalanceModal.rows) ? overallBalanceModal.rows : [];
    const searchValue = String(overallBalanceFilter || "").trim().toLowerCase();
    const filteredRows = searchValue
      ? rows.filter((row) => String(row?.label || "").toLowerCase().includes(searchValue))
      : rows;
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / OVERALL_BALANCE_PAGE_SIZE));
    const safePage = Math.min(Math.max(Number(overallBalancePage) || 1, 1), totalPages);
    const startIndex = (safePage - 1) * OVERALL_BALANCE_PAGE_SIZE;
    const pagedRows = filteredRows.slice(startIndex, startIndex + OVERALL_BALANCE_PAGE_SIZE);
    const visibleStart = filteredRows.length === 0 ? 0 : startIndex + 1;
    const visibleEnd =
      filteredRows.length === 0 ? 0 : Math.min(startIndex + OVERALL_BALANCE_PAGE_SIZE, filteredRows.length);
    const totalAmount = filteredRows.reduce((sum, row) => sum + (Number(row?.totalAmount) || 0), 0);
    const totalPaidAmount = filteredRows.reduce((sum, row) => sum + (Number(row?.paidAmount) || 0), 0);
    const totalKnockOffAmount = filteredRows.reduce((sum, row) => sum + (Number(row?.knockOffAmount) || 0), 0);
    const totalBalanceAmount = filteredRows.reduce((sum, row) => sum + (Number(row?.balanceAmount) || 0), 0);
    const columnDefinitions = [
      {
        key: "entries",
        label: "Entries",
        headerClassName: "text-right",
        render: (row) => row.entries,
        cellClassName: "text-right",
      },
      {
        key: "totalAmount",
        label: overallBalanceModal.amountLabel,
        headerClassName: "text-right",
        render: (row) => `Rs ${formatMoney(row.totalAmount)}`,
        cellClassName: "text-right",
      },
      {
        key: "paidAmount",
        label: "Total Paid",
        headerClassName: "text-right",
        render: (row) => `Rs ${formatMoney(row.paidAmount)}`,
        cellClassName: "text-right overall-balance-table__paid",
      },
      {
        key: "knockOffAmount",
        label: "Total Knock Off",
        headerClassName: "text-right",
        render: (row) => `Rs ${formatMoney(row.knockOffAmount)}`,
        cellClassName: "text-right overall-balance-table__knock-off",
      },
      {
        key: "balanceAmount",
        label: "Balance",
        headerClassName: "text-right",
        render: (row) => `Rs ${formatMoney(row.balanceAmount)}`,
        cellClassName: "text-right overall-balance-table__balance",
      },
    ];
    const visibleColumns = columnDefinitions.filter((column) => overallBalanceVisibleColumns?.[column.key] !== false);
    const tableColumnCount = visibleColumns.length + 1;

    return (
      <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeOverallBalanceModal}>
        <div
          className="modal-card overall-balance-modal"
          style={{ maxWidth: "1080px" }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="card-header" style={{ justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div>
              <h3 className="modal-title" style={{ margin: 0 }}>
                {overallBalanceModal.title}
              </h3>
              <div className="dashboard-table-panel__meta" style={{ marginTop: 4 }}>
                Start to till date balance summary
              </div>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={closeOverallBalanceModal}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem" }}
            >
              Close
            </button>
          </div>

          <div className="sales-pulse-summary-strip">
            <div className="sales-pulse-summary-pill">
              <span>{`${overallBalanceModal.entityLabel}s`}</span>
              <strong>{filteredRows.length}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>{overallBalanceModal.amountLabel}</span>
              <strong>{`Rs ${formatMoney(totalAmount)}`}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>Total Paid</span>
              <strong>{`Rs ${formatMoney(totalPaidAmount)}`}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>Total Knock Off</span>
              <strong>{`Rs ${formatMoney(totalKnockOffAmount)}`}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>Total Balance</span>
              <strong>{`Rs ${formatMoney(totalBalanceAmount)}`}</strong>
            </div>
          </div>

          <div className="dashboard-table-panel" style={{ marginTop: 16 }}>
            <div className="dashboard-table-panel__head">
              <div className="dashboard-table-panel__title-wrap">
                <h3>{`${overallBalanceModal.entityLabel}-wise Lifetime Balance`}</h3>
                <span className="dashboard-table-panel__meta">
                  {searchValue
                    ? `Showing ${visibleStart}-${visibleEnd} of ${filteredRows.length} filtered ${overallBalanceModal.entityLabel.toLowerCase()}s`
                    : `Showing ${visibleStart}-${visibleEnd} of ${filteredRows.length} ${overallBalanceModal.entityLabel.toLowerCase()}s`}
                </span>
              </div>
            </div>
            <div className="overall-balance-modal__toolbar">
              <div className="overall-balance-modal__filter">
                <input
                  type="search"
                  className="input-field"
                  value={overallBalanceFilter}
                  onChange={(event) => {
                    setOverallBalanceFilter(event.target.value);
                    setOverallBalancePage(1);
                  }}
                  placeholder={`Filter ${overallBalanceModal.entityLabel.toLowerCase()}...`}
                />
              </div>
              <div className="overall-balance-modal__columns">
                {columnDefinitions.map((column) => (
                  <label key={column.key} className="overall-balance-modal__column-toggle">
                    <input
                      type="checkbox"
                      checked={overallBalanceVisibleColumns?.[column.key] !== false}
                      onChange={() =>
                        setOverallBalanceVisibleColumns((prev) => ({
                          ...prev,
                          [column.key]: !(prev?.[column.key] !== false),
                        }))
                      }
                    />
                    <span>{column.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="dashboard-table-scroll">
              <table className="dashboard-sales-table supplier-ledger-table overall-balance-table">
                <thead>
                  <tr>
                    <th>{overallBalanceModal.entityLabel}</th>
                    {visibleColumns.map((column) => (
                      <th key={column.key} className={column.headerClassName || ""}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumnCount} className="empty-state" style={{ padding: 16 }}>
                        No balance records found.
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        {visibleColumns.map((column) => (
                          <td key={`${row.label}-${column.key}`} className={column.cellClassName || ""}>
                            {column.render(row)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="form-actions" style={{ justifyContent: "space-between", marginTop: 14 }}>
              <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                {searchValue ? `${filteredRows.length} filtered / ${rows.length} total records` : `${rows.length} records`}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={safePage <= 1}
                  onClick={() => setOverallBalancePage((page) => Math.max(1, page - 1))}
                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                >
                  Prev
                </button>
                <span style={{ minWidth: 90, textAlign: "center", color: "#475569", fontSize: "0.85rem" }}>
                  Page {safePage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={safePage >= totalPages}
                  onClick={() => setOverallBalancePage((page) => Math.min(totalPages, page + 1))}
                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSupplierQtyBreakdownModal = () => {
    if (!supplierQtyBreakdownOpen) return null;

    const modalLabel = isOverallFilter(selectedSupplierName)
      ? selectedSupplierOverallValues.length === supplierOptions.length
        ? "All Suppliers"
        : `${selectedSupplierOverallValues.length} Selected Suppliers`
      : displayDashboardFilterValue(selectedSupplierName) || "-";
    const includeSupplierColumn = supplierPurchaseQtyBreakdown.includeSupplierColumn;
    const detailColSpan = includeSupplierColumn ? 6 : 5;

    return (
      <div
        className="modal-backdrop"
        role="dialog"
        aria-modal="true"
        onClick={() => setSupplierQtyBreakdownOpen(false)}
      >
        <div
          className="modal-card supplier-qty-breakdown-modal"
          style={{ maxWidth: "1080px" }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="card-header"
            style={{ justifyContent: "space-between", alignItems: "center", gap: 12 }}
          >
            <div>
              <h3 className="modal-title" style={{ margin: 0 }}>
                Purchase Qty Breakup
              </h3>
              <div className="dashboard-table-panel__meta" style={{ marginTop: 4 }}>
                {`${modalLabel} | ${formatMonthLabel(selectedSupplierMonth)}`}
              </div>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setSupplierQtyBreakdownOpen(false)}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem" }}
            >
              Close
            </button>
          </div>

          <div className="sales-pulse-summary-strip">
            <div className="sales-pulse-summary-pill">
              <span>Total Qty</span>
              <strong>{supplierPurchaseQtyBreakdown.totalQty.toFixed(2)}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>Total Amount</span>
              <strong>{`Rs ${formatMoney(supplierPurchaseQtyBreakdown.totalAmount)}`}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>Categories</span>
              <strong>{supplierPurchaseQtyBreakdown.categoryCount}</strong>
            </div>
            <div className="sales-pulse-summary-pill">
              <span>Dates</span>
              <strong>{supplierPurchaseQtyBreakdown.dateCount}</strong>
            </div>
          </div>

          <div className="dashboard-table-panel" style={{ marginTop: 16 }}>
            <div className="dashboard-table-panel__head">
              <div className="dashboard-table-panel__title-wrap">
                <h3>Date-wise Category Breakup</h3>
                <span className="dashboard-table-panel__meta">
                  Week-wise category totals with qty and amount
                </span>
              </div>
            </div>
            <div className="dashboard-table-scroll supplier-qty-breakdown-scroll">
              <table className="dashboard-sales-table supplier-ledger-table">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Date</th>
                    {includeSupplierColumn ? <th>Supplier</th> : null}
                    <th>Category</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierPurchaseQtyBreakdown.rows.length === 0 ? (
                    <tr>
                      <td colSpan={detailColSpan} className="empty-state" style={{ padding: 16 }}>
                        No purchase qty breakup found for {formatMonthLabel(selectedSupplierMonth)}.
                      </td>
                    </tr>
                  ) : (
                    supplierPurchaseQtyBreakdown.rows.map((row) => (
                      <tr key={`${row.weekKey}-${row.date}-${row.category}-${row.supplierName || ""}`}>
                        <td>{row.weekLabel}</td>
                        <td>{formatDisplayDate(row.date)}</td>
                        {includeSupplierColumn ? <td>{row.supplierName}</td> : null}
                        <td>{row.category}</td>
                        <td className="text-right">{row.qty.toFixed(2)}</td>
                        <td className="text-right">{`Rs ${formatMoney(row.amount)}`}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="dashboard-table-panel" style={{ marginTop: 16 }}>
            <div className="dashboard-table-panel__head">
              <div className="dashboard-table-panel__title-wrap">
                <h3>Week-wise Totals</h3>
              </div>
            </div>
            <div className="dashboard-table-scroll">
              <table className="dashboard-sales-table supplier-ledger-table">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierPurchaseQtyBreakdown.weekTotals.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="empty-state" style={{ padding: 16 }}>
                        No week-wise totals available.
                      </td>
                    </tr>
                  ) : (
                    supplierPurchaseQtyBreakdown.weekTotals.map((row) => (
                      <tr key={row.weekKey}>
                        <td>{row.weekLabel}</td>
                        <td className="text-right">{row.qty.toFixed(2)}</td>
                        <td className="text-right">{`Rs ${formatMoney(row.amount)}`}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-vibrant">
      <div className="dashboard-vibrant__bg" aria-hidden="true" />
      <div className="dashboard-vibrant__shell">
        <div className="dashboard-topbar">
          <div>
            <div className="dashboard-kicker">Operations Dashboard</div>
            <h1 className="dashboard-title">
              {!isMonthSummaryTab
                ? "Sales Pulse Dashboard"
                : isSupplierSummaryTab
                ? "Supplier Weekly Summary"
                : isStallCreditSummaryTab
                  ? "Delivery Billing Summary"
                  : "Stall Billing Summary"}
            </h1>
            <div className="dashboard-subtitle">
              {isSupplierSummaryTab ? (
                <>
                  Weekly purchase view for <strong>{formatMonthLabel(selectedSupplierMonth)}</strong>
                  {selectedSupplierName ? (
                    <>
                      {" "}
                      {isOverallFilter(selectedSupplierName) ? (
                        <>
                          across{" "}
                          <strong>
                            {selectedSupplierOverallValues.length === supplierOptions.length
                              ? "all suppliers"
                              : `${selectedSupplierOverallValues.length} selected suppliers`}
                          </strong>
                        </>
                      ) : (
                        <>
                          for <strong>{displayDashboardFilterValue(selectedSupplierName)}</strong>
                        </>
                      )}
                    </>
                  ) : null}
                </>
              ) : isStallCreditSummaryTab ? (
                <>
                  Weekly delivery billing view for <strong>{formatMonthLabel(selectedSupplierMonth)}</strong>
                  {selectedStallCustomerName ? (
                    <>
                      {" "}
                      {isOverallFilter(selectedStallCustomerName) ? (
                        <>
                          across{" "}
                          <strong>
                            {selectedStallOverallValues.length === stallCreditCustomerOptions.length
                              ? "all customers"
                              : `${selectedStallOverallValues.length} selected customers`}
                          </strong>
                        </>
                      ) : (
                        <>
                          for <strong>{displayDashboardFilterValue(selectedStallCustomerName)}</strong>
                        </>
                      )}
                    </>
                  ) : null}
                </>
              ) : isMarketCreditSummaryTab ? (
                <>
                  Weekly stall billing view for <strong>{formatMonthLabel(selectedSupplierMonth)}</strong>
                  {selectedMarketCustomerName ? (
                    <>
                      {" "}
                      {isOverallFilter(selectedMarketCustomerName) ? (
                        <>
                          across{" "}
                          <strong>
                            {selectedMarketOverallValues.length === marketCreditCustomerOptions.length
                              ? "all customers"
                              : `${selectedMarketOverallValues.length} selected customers`}
                          </strong>
                        </>
                      ) : (
                        <>
                          for <strong>{displayDashboardFilterValue(selectedMarketCustomerName)}</strong>
                        </>
                      )}
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  Sales view from <strong>{formatDisplayDate(salesRangeStart)}</strong> to{" "}
                  <strong>{formatDisplayDate(salesRangeEnd)}</strong> across Delivery Billing, Stall Credit / Retail / Evening,
                  and Market Credit / Retail.
                  {" "}
                  <strong>{activeSalesBucketLabel}</strong>
                </>
              )}
            </div>
          </div>
          <div className="dashboard-topbar-actions">
            {isMonthSummaryTab ? (
              <>
                <div className="dashboard-date-filter" ref={monthFilterRef}>
                  <span className="dashboard-date-filter__label">Month Filter</span>
                  <div className="dashboard-date-filter__control">
                    <button
                      type="button"
                      className="dashboard-date-filter__trigger"
                      onClick={openDashboardPicker}
                      aria-label="Choose month"
                      aria-haspopup="dialog"
                      aria-expanded={monthPickerOpen}
                    >
                      <span className="dashboard-date-filter__value">
                        {formatMonthLabel(selectedSupplierMonth).replace(/ (\d{4})$/, ", $1")}
                      </span>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </button>
                    {monthPickerOpen ? (
                      <div className="dashboard-month-picker" role="dialog" aria-label="Choose month and year">
                        <div className="dashboard-month-picker__year-row">
                          <button
                            type="button"
                            onClick={() => setMonthPickerYear((year) => Math.max(1900, year - 1))}
                            disabled={monthPickerYear <= 1900}
                            aria-label="Previous year"
                          >
                            ‹
                          </button>
                          <strong>{monthPickerYear}</strong>
                          <button
                            type="button"
                            onClick={() => setMonthPickerYear((year) => Math.min(Number(currentMonthKey.slice(0, 4)), year + 1))}
                            disabled={monthPickerYear >= Number(currentMonthKey.slice(0, 4))}
                            aria-label="Next year"
                          >
                            ›
                          </button>
                        </div>
                        <div className="dashboard-month-picker__months">
                          {DASHBOARD_MONTH_NAMES.map((monthName, index) => {
                            const monthKey = `${monthPickerYear}-${String(index + 1).padStart(2, "0")}`;
                            return (
                              <button
                                key={monthKey}
                                type="button"
                                className={monthKey === selectedSupplierMonth ? "selected" : ""}
                                disabled={monthKey > currentMonthKey}
                                onClick={() => {
                                  setSelectedSupplierMonth(monthKey);
                                  setMonthPickerOpen(false);
                                }}
                                aria-label={`${monthName} ${monthPickerYear}`}
                                aria-pressed={monthKey === selectedSupplierMonth}
                              >
                                {monthName.slice(0, 3)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="dashboard-date-reset-btn"
                  onClick={() => {
                    setSelectedSupplierMonth(currentMonthKey);
                    setMonthPickerOpen(false);
                  }}
                  disabled={selectedSupplierMonth === currentMonthKey}
                >
                  Current Month
                </button>
              </>
            ) : (
              <button
                type="button"
                className="dashboard-date-reset-btn"
                onClick={resetSalesPulseFilters}
                disabled={!salesFiltersDirty}
              >
                Reset Filters
              </button>
            )}
            <button
              type="button"
              className="dashboard-refresh-btn"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh Data"}
            </button>
          </div>
        </div>

        {error ? <div className="dashboard-alert">{error}</div> : null}

        <div className="dashboard-view-tabs" role="tablist" aria-label="Dashboard views">

          <button
            type="button"
            className={`dashboard-view-tab ${isSupplierSummaryTab ? "active" : ""}`}
            onClick={() => setActiveTab("supplier-summary")}
            role="tab"
            aria-selected={isSupplierSummaryTab}
          >
            Supplier Summary
          </button>
          <button
            type="button"
            className={`dashboard-view-tab ${isStallCreditSummaryTab ? "active" : ""}`}
            onClick={() => setActiveTab("stall-credit-summary")}
            role="tab"
            aria-selected={isStallCreditSummaryTab}
          >
            Delivery Billing Summary
          </button>
          <button
            type="button"
            className={`dashboard-view-tab ${isMarketCreditSummaryTab ? "active" : ""}`}
            onClick={() => setActiveTab("market-credit-summary")}
            role="tab"
            aria-selected={isMarketCreditSummaryTab}
          >
            Stall Billing Summary
          </button>
        </div>

        {!isMonthSummaryTab ? (
          <>
            <div className="dashboard-breakdown-panel sales-pulse-panel">
              <div className="dashboard-breakdown-header">
                <div>
                  <h2>Sales Search And Summary</h2>
                  <div>
                    {isSalesCurrentDayRange
                      ? `Showing all sales streams for ${formatDisplayDate(salesRangeStart)}`
                      : `Showing all sales streams from ${formatDisplayDate(salesRangeStart)} to ${formatDisplayDate(
                          salesRangeEnd
                        )}`}
                  </div>
                </div>
                <div className="sales-pulse-summary-strip">
                  <div className="sales-pulse-summary-pill">
                    <span>Total Amount</span>
                    <strong>{`Rs ${formatMoney(salesPulseTotals.amount)}`}</strong>
                  </div>
                  <div className="sales-pulse-summary-pill">
                    <span>Total Qty</span>
                    <strong>{Number(salesPulseTotals.qty || 0).toFixed(2)}</strong>
                  </div>
                  <div className="sales-pulse-summary-pill">
                    <span>Rows</span>
                    <strong>{salesPulseTotals.entries}</strong>
                  </div>
                </div>
              </div>

              <div className="sales-pulse-filter-grid">
                <label className="field sales-pulse-filter-field">
                  <span>From Date</span>
                  <input
                    className="input-field"
                    type="date"
                    value={salesFromDate}
                    max={currentDateKey}
                    onChange={(event) => {
                      const nextValue = normalizeYmd(event?.target?.value);
                      if (!nextValue || nextValue > currentDateKey) return;
                      setSalesFromDate(nextValue);
                    }}
                  />
                </label>
                <label className="field sales-pulse-filter-field">
                  <span>To Date</span>
                  <input
                    className="input-field"
                    type="date"
                    value={salesToDate}
                    max={currentDateKey}
                    onChange={(event) => {
                      const nextValue = normalizeYmd(event?.target?.value);
                      if (!nextValue || nextValue > currentDateKey) return;
                      setSalesToDate(nextValue);
                    }}
                  />
                </label>
                <label className="field sales-pulse-filter-field">
                  <span>Segment</span>
                  <select
                    className="select-field"
                    value={salesSegmentFilter}
                    onChange={(event) => setSalesSegmentFilter(event.target.value)}
                  >
                    <option value="All">All Segments</option>
                    <option value="Delivery">Delivery</option>
                    <option value="Stall">Stall</option>
                    <option value="Market">Market</option>
                  </select>
                </label>
                <label className="field sales-pulse-filter-field">
                  <span>Sales Stream</span>
                  <select
                    className="select-field"
                    value={salesBucketFilter}
                    onChange={(event) => setSalesBucketFilter(event.target.value)}
                  >
                    <option value="all">All Streams</option>
                    {SALES_PULSE_BUCKETS.map((bucket) => (
                      <option key={bucket.key} value={bucket.key}>
                        {bucket.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field sales-pulse-filter-field">
                  <span>Payment Type</span>
                  <select
                    className="select-field"
                    value={salesPaymentTypeFilter}
                    onChange={(event) => setSalesPaymentTypeFilter(event.target.value)}
                  >
                    {salesPaymentTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field sales-pulse-filter-field">
                  <span>Party Search</span>
                  <input
                    className="input-field"
                    type="text"
                    value={salesPartySearch}
                    onChange={(event) => setSalesPartySearch(event.target.value)}
                    placeholder="Customer / supplier / bill / reference"
                  />
                </label>
                <label className="field sales-pulse-filter-field sales-pulse-filter-field--wide">
                  <span>Material Search</span>
                  <input
                    className="input-field"
                    type="text"
                    value={salesMaterialSearch}
                    onChange={(event) => setSalesMaterialSearch(event.target.value)}
                    placeholder="Search by material name"
                  />
                </label>
              </div>

              <div className="sales-pulse-card-grid">
                {salesPulseCards.map((card) => {
                  const selected = salesBucketFilter === card.key;
                  return (
                    <button
                      key={card.key}
                      type="button"
                      className={`sales-pulse-card${selected ? " is-selected" : ""}`}
                      style={{
                        borderColor: `${card.accent}66`,
                        background: `linear-gradient(135deg, ${card.soft}, rgba(15, 23, 42, 0.82))`,
                      }}
                      onClick={() => setSalesBucketFilter((current) => (current === card.key ? "all" : card.key))}
                    >
                      <div className="sales-pulse-card__head">
                        <div className="sales-pulse-card__title">
                          <span className="sales-pulse-card__dot" style={{ background: card.accent }} aria-hidden="true" />
                          {card.label}
                        </div>
                        <div className="sales-pulse-card__pill">{card.entries} rows</div>
                      </div>
                      <div className="sales-pulse-card__amount">{`Rs ${formatMoney(card.amount)}`}</div>
                      <div className="sales-pulse-card__footer">
                        <span>{`Qty ${Number(card.qty || 0).toFixed(2)}`}</span>
                        <span>{selected ? "Active Filter" : "Click To Filter"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="dashboard-table-panel sales-pulse-table-panel">
              <div className="dashboard-table-panel__head">
                <div className="dashboard-table-panel__title-wrap">
                  <h3>Sales Details</h3>
                  <span className="dashboard-table-panel__meta">
                    {`${activeSalesBucketLabel} | ${salesPulseDisplayRows.length} ${isDeliveryBillingCategoryView ? "categories" : "rows"}`}
                  </span>
                </div>
                <div>{`Range ${formatDisplayDate(salesRangeStart)} to ${formatDisplayDate(salesRangeEnd)}`}</div>
              </div>
              {salesBucketFilter === "stall-retail" && stallRetailSalesBreakup ? (
                <div
                  className="sales-pulse-summary-strip"
                  style={{ padding: "0 1.5rem 1rem", justifyContent: "flex-start" }}
                >
                  <div className="sales-pulse-summary-pill">
                    <span>Actual Stall Retail</span>
                    <strong>{`Rs ${formatMoney(stallRetailSalesBreakup.actualAmount)}`}</strong>
                    <small>{`Qty ${Number(stallRetailSalesBreakup.actualQty || 0).toFixed(2)}`}</small>
                  </div>
                  <div className="sales-pulse-summary-pill">
                    <span>{`Less Delivery Billing (${STALL_RETAIL_DELIVERY_GROUPS.join(", ")})`}</span>
                    <strong>{`Rs ${formatMoney(stallRetailSalesBreakup.deliveryDeductionAmount)}`}</strong>
                    <small>{`Qty ${Number(stallRetailSalesBreakup.deliveryDeductionQty || 0).toFixed(2)}`}</small>
                  </div>
                  <div className="sales-pulse-summary-pill">
                    <span>Net Stall Retail</span>
                    <strong>{`Rs ${formatMoney(stallRetailSalesBreakup.netAmount)}`}</strong>
                    <small>{`Qty ${Number(stallRetailSalesBreakup.netQty || 0).toFixed(2)}`}</small>
                  </div>
                </div>
              ) : null}
              <div className="dashboard-table-scroll">
                <table className="dashboard-sales-table">
                  <thead>
                    {isDeliveryBillingCategoryView ? (
                      <tr>
                        <th>Category</th>
                        <th>Mapped Item Groups</th>
                        <th className="text-right">Bills</th>
                        <th className="text-right">Items</th>
                        <th className="text-right">Qty</th>
                        <th className="text-right">Amount</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Stream</th>
                        <th>Date</th>
                        <th>Reference</th>
                        <th>Party</th>
                        <th>Materials</th>
                        <th className="text-right">Items</th>
                        <th className="text-right">Qty</th>
                        <th className="text-right">Amount</th>
                        <th>Payment Type</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {salesPulseDisplayRows.length === 0 ? (
                      <tr>
                        <td colSpan={isDeliveryBillingCategoryView ? 6 : 9} className="empty-state" style={{ padding: 16 }}>
                          {isDeliveryBillingCategoryView
                            ? "No delivery billing category data found for the selected filters."
                            : "No sales data found for the selected filters."}
                        </td>
                      </tr>
                    ) : isDeliveryBillingCategoryView ? (
                      pagedSelectedEntries.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <span
                              className="category-pill"
                              style={{
                                background: row.soft,
                                color: row.accent,
                                borderColor: `${row.accent}55`,
                              }}
                            >
                              {row.categoryLabel}
                            </span>
                          </td>
                          <td>{row.categoryGroups}</td>
                          <td className="text-right">{Number(row.billCount || 0)}</td>
                          <td className="text-right">{Number(row.itemCount || 0)}</td>
                          <td className="text-right">{Number(row.qty || 0).toFixed(2)}</td>
                          <td className="text-right dashboard-sales-table__amount">{`Rs ${formatMoney(row.amount)}`}</td>
                        </tr>
                      ))
                    ) : (
                      pagedSelectedEntries.map((row) => (
                        <tr key={row.id}>
                          {(() => {
                            const isStallRetailRow = row.bucketKey === "stall-retail";
                            const actualQty = Number((row.actualQty ?? row.qty) || 0);
                            const deductionQty = Number(row.deliveryDeductionQty || 0);
                            const netQty = Number((row.netQty ?? row.qty) || 0);
                            const referenceText = isStallRetailRow
                              ? `Actual Rs ${formatMoney(row.actualAmount ?? row.amount)} | Less Delivery Rs ${formatMoney(
                                  row.deliveryDeductionAmount || 0
                                )}`
                              : row.reference || "-";
                            const partyText = isStallRetailRow
                              ? `Qty Actual ${actualQty.toFixed(2)} | Less Delivery ${deductionQty.toFixed(2)} | Net ${netQty.toFixed(2)}`
                              : row.party || "-";
                            return (
                              <>
                          <td>
                            <span
                              className="category-pill"
                              style={{
                                background: row.soft,
                                color: row.accent,
                                borderColor: `${row.accent}55`,
                              }}
                            >
                              {row.bucketLabel}
                            </span>
                          </td>
                          <td>{formatDisplayDate(row.date)}</td>
                          <td>{referenceText}</td>
                          <td>{partyText}</td>
                          <td className="sales-pulse-materials" title={row.materialsText || "-"}>
                            {row.materialsText || "-"}
                          </td>
                          <td className="text-right">{Number(row.itemCount || 0)}</td>
                          <td className="text-right">{Number(row.qty || 0).toFixed(2)}</td>
                          <td className="text-right dashboard-sales-table__amount">{`Rs ${formatMoney(row.amount)}`}</td>
                          <td>
                            <span className={`payment-type-pill payment-type-pill--${paymentTypeClassName(row.paymentType)}`}>
                              {row.paymentType}
                            </span>
                          </td>
                              </>
                            );
                          })()}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {salesPulseDisplayRows.length > 0 ? (
                <div
                  className="dashboard-table-panel__head"
                  style={{ paddingTop: 14, borderTop: "1px solid rgba(148, 163, 184, 0.18)" }}
                >
                  <div>{`Showing ${salesBreakdownPageStart}-${salesBreakdownPageEnd} of ${salesPulseDisplayRows.length} ${isDeliveryBillingCategoryView ? "categories" : "rows"}`}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSalesBreakdownPage((page) => Math.max(1, page - 1))}
                      disabled={salesBreakdownPage <= 1}
                    >
                      Prev
                    </button>
                    <span className="entry-count">{`Page ${salesBreakdownPage} / ${salesBreakdownTotalPages}`}</span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSalesBreakdownPage((page) => Math.min(salesBreakdownTotalPages, page + 1))}
                      disabled={salesBreakdownPage >= salesBreakdownTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {isCreditSalesPaymentView ? (
              <div className="dashboard-table-panel sales-pulse-table-panel">
                <div className="dashboard-table-panel__head">
                  <div className="dashboard-table-panel__title-wrap">
                    <h3>Payment Details</h3>
                    <span className="dashboard-table-panel__meta">
                      {`${activeSalesBucketLabel} | ${salesPulsePaymentRows.length} rows`}
                    </span>
                  </div>
                  <div>{`Payment Date Range ${formatDisplayDate(salesRangeStart)} to ${formatDisplayDate(salesRangeEnd)}`}</div>
                </div>
                {salesPulsePaymentRows.length > 0 ? (
                  <div
                    className="sales-pulse-summary-strip"
                    style={{ padding: "0 1.5rem 1rem", justifyContent: "flex-start" }}
                  >
                    <div className="sales-pulse-summary-pill">
                      <span>Total Paid</span>
                      <strong>{`Rs ${formatMoney(salesPulsePaymentTotals.amount)}`}</strong>
                    </div>
                    <div className="sales-pulse-summary-pill">
                      <span>Customers</span>
                      <strong>{salesPulsePaymentTotals.customerCount}</strong>
                    </div>
                    <div className="sales-pulse-summary-pill">
                      <span>Payment Entries</span>
                      <strong>{salesPulsePaymentTotals.paymentCount}</strong>
                    </div>
                  </div>
                ) : null}
                <div className="dashboard-table-scroll">
                  <table className="dashboard-sales-table">
                    <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Customer</th>
                        <th>Against Sales Dates</th>
                        <th className="text-right">Payment Rows</th>
                        <th>Modes</th>
                        <th className="text-right">Paid Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesPulsePaymentRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="empty-state" style={{ padding: 16 }}>
                            No paid data found for the selected filters.
                          </td>
                        </tr>
                      ) : (
                        pagedSalesPaymentRows.map((row) => (
                          <tr key={row.id}>
                            <td>{formatDisplayDate(row.paymentDate)}</td>
                            <td>{row.customer || "-"}</td>
                            <td className="sales-pulse-materials" title={row.relatedSalesDatesText || "-"}>
                              {row.relatedSalesDatesText || "-"}
                            </td>
                            <td className="text-right">{Number(row.paymentCount || 0)}</td>
                            <td>{row.paymentModesText}</td>
                            <td className="text-right dashboard-sales-table__amount">{`Rs ${formatMoney(row.amount)}`}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {salesPulsePaymentRows.length > 0 ? (
                  <div
                    className="dashboard-table-panel__head"
                    style={{ paddingTop: 14, borderTop: "1px solid rgba(148, 163, 184, 0.18)" }}
                  >
                    <div>{`Showing ${salesPaymentDetailsPageStart}-${salesPaymentDetailsPageEnd} of ${salesPulsePaymentRows.length} rows`}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setSalesPaymentDetailsPage((page) => Math.max(1, page - 1))}
                        disabled={salesPaymentDetailsPage <= 1}
                      >
                        Prev
                      </button>
                      <span className="entry-count">{`Page ${salesPaymentDetailsPage} / ${salesPaymentDetailsTotalPages}`}</span>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setSalesPaymentDetailsPage((page) => Math.min(salesPaymentDetailsTotalPages, page + 1))}
                        disabled={salesPaymentDetailsPage >= salesPaymentDetailsTotalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : isSupplierSummaryTab ? (
          renderFinancialSummaryPanel({
            filterLabel: "Supplier",
            overallFilterLabel: "Included Suppliers",
            emptyOptionLabel: "No Suppliers",
            selectedName: selectedSupplierName,
            onSelectName: handleSelectSupplierName,
            overallSelectedValues: selectedSupplierOverallValues,
            onToggleOverallValue: toggleSupplierOverallValue,
            onSelectAllOverall: () => {
              setSelectedSupplierOverallNames(null);
              setSupplierOverallBreakdown(makeOverallBreakdownState());
            },
            selectedWeekKey: selectedSupplierWeekKey,
            onSelectWeek: setSelectedSupplierWeekKey,
            options: supplierOptions,
            summaryMatrix: supplierSummaryMatrix,
            summaryEntries: supplierEntriesForSummary,
            ledgerSummary: supplierLedgerSummary,
            totalLabel: "Total Purchase",
            quantityLabel: "Total Qty",
            quantityValue: supplierSummaryMatrix.totalQty,
            onOpenQuantityBreakdown: () => setSupplierQtyBreakdownOpen(true),
            ledgerAmountLabel: "Purchase",
            openPaymentModal: openSupplierPaymentModal,
            emptyMessage: "No suppliers available for summary",
            getEntryDate: (entry) => entry?.createdAt,
            getBreakdownLabel: (entry) => String(entry?.supplierName || "").trim() || "Unknown Supplier",
            overallBreakdown: supplierOverallBreakdown,
            onOpenOverallBreakdown: setSupplierOverallBreakdown,
            onCloseOverallBreakdown: () => setSupplierOverallBreakdown(makeOverallBreakdownState()),
            historyPage: supplierSettlementHistoryPage,
            onChangeHistoryPage: setSupplierSettlementHistoryPage,
            revertingHistoryPaymentId: supplierRevertingSettlementId,
            onRevertHistoryRow: revertSupplierKnockOff,
            onOpenOverallBalance: () =>
              openOverallBalanceModal({
                title: "Supplier Overall Balance",
                entityLabel: "Supplier",
                amountLabel: "Total Purchase",
                rows: supplierOverallBalanceRows,
              }),
            overallBalanceDisabled: supplierOverallBalanceRows.length === 0,
          })
        ) : isStallCreditSummaryTab ? (
          renderFinancialSummaryPanel({
            filterLabel: "Customer",
            overallFilterLabel: "Included Customers",
            emptyOptionLabel: "No Customers",
            selectedName: selectedStallCustomerName,
            onSelectName: handleSelectStallCustomerName,
            overallSelectedValues: selectedStallOverallValues,
            onToggleOverallValue: toggleStallOverallValue,
            onSelectAllOverall: () => {
              setSelectedStallOverallCustomers(null);
              setStallOverallBreakdown(makeOverallBreakdownState());
            },
            selectedWeekKey: selectedStallWeekKey,
            onSelectWeek: setSelectedStallWeekKey,
            options: stallCreditCustomerOptions,
            summaryMatrix: stallCreditSummaryMatrix,
            summaryEntries: stallCreditEntriesForSummary,
            ledgerSummary: stallCreditLedgerSummary,
            totalLabel: "Total Sales",
            quantityLabel: "",
            quantityValue: 0,
            onOpenQuantityBreakdown: null,
            ledgerAmountLabel: "Sales",
            openPaymentModal: openStallCreditPaymentModal,
            emptyMessage: "No delivery billing customers available for summary",
            getEntryDate: (entry) => entry?.saleDate,
            getBreakdownLabel: (entry) => dashboardCustomerLabel(entry?.customer),
            overallBreakdown: stallOverallBreakdown,
            onOpenOverallBreakdown: setStallOverallBreakdown,
            onCloseOverallBreakdown: () => setStallOverallBreakdown(makeOverallBreakdownState()),
            historyPage: stallSettlementHistoryPage,
            onChangeHistoryPage: setStallSettlementHistoryPage,
            revertingHistoryPaymentId: stallRevertingSettlementId,
            onRevertHistoryRow: revertStallKnockOff,
            onOpenOverallBalance: () =>
              openOverallBalanceModal({
                title: "Sales Overall Balance",
                entityLabel: "Customer",
                amountLabel: "Total Sales",
                rows: stallCreditOverallBalanceRows,
              }),
            overallBalanceDisabled: stallCreditOverallBalanceRows.length === 0,
          })
        ) : (
          renderFinancialSummaryPanel({
            filterLabel: "Customer",
            overallFilterLabel: "Included Customers",
            emptyOptionLabel: "No Customers",
            selectedName: selectedMarketCustomerName,
            onSelectName: handleSelectMarketCustomerName,
            overallSelectedValues: selectedMarketOverallValues,
            onToggleOverallValue: toggleMarketOverallValue,
            onSelectAllOverall: () => {
              setSelectedMarketOverallCustomers(null);
              setMarketOverallBreakdown(makeOverallBreakdownState());
            },
            selectedWeekKey: selectedMarketWeekKey,
            onSelectWeek: setSelectedMarketWeekKey,
            options: marketCreditCustomerOptions,
            summaryMatrix: marketCreditSummaryMatrix,
            summaryEntries: marketCreditEntriesForSummary,
            ledgerSummary: marketCreditLedgerSummary,
            totalLabel: "Total Sales",
            quantityLabel: "",
            quantityValue: 0,
            onOpenQuantityBreakdown: null,
            ledgerAmountLabel: "Sales",
            openPaymentModal: openMarketCreditPaymentModal,
            emptyMessage: "No stall billing customers available for summary",
            getEntryDate: (entry) => entry?.saleDate,
            getBreakdownLabel: (entry) => dashboardCustomerLabel(entry?.customer),
            overallBreakdown: marketOverallBreakdown,
            onOpenOverallBreakdown: setMarketOverallBreakdown,
            onCloseOverallBreakdown: () => setMarketOverallBreakdown(makeOverallBreakdownState()),
            historyPage: marketSettlementHistoryPage,
            onChangeHistoryPage: setMarketSettlementHistoryPage,
            revertingHistoryPaymentId: marketRevertingSettlementId,
            onRevertHistoryRow: revertMarketKnockOff,
            onOpenOverallBalance: () =>
              openOverallBalanceModal({
                title: "Stall Billing Overall Balance",
                entityLabel: "Customer",
                amountLabel: "Total Sales",
                rows: marketCreditOverallBalanceRows,
              }),
            overallBalanceDisabled: marketCreditOverallBalanceRows.length === 0,
          })
        )}

        {renderSupplierQtyBreakdownModal()}
        {renderOverallBalanceModal()}
        {renderDashboardPaymentModal({
          open: supplierPaymentModal.open,
          viewOnly: supplierPaymentModal.viewOnly,
          title: supplierPaymentModal.viewOnly ? "View Supplier Settlement" : "Add Supplier Settlement",
          nameLabel: "Supplier",
          nameValue: supplierPaymentModal.supplierName,
          label: supplierPaymentModal.label,
          totalAmount: supplierPaymentModal.totalAmount,
          paidAmount: supplierPaymentModal.paidAmount,
          paidDateSummary: supplierPaymentModal.paidDateSummary,
          discountAmount: supplierPaymentModal.discountAmount,
          knockOffAmount: supplierPaymentModal.knockOffAmount,
          pendingAmount: supplierPaymentModal.pendingAmount,
          form: supplierPaymentForm,
          onChangeForm: setSupplierPaymentForm,
          saving: supplierPaymentSaving,
          onClose: closeSupplierPaymentModal,
          onSave: saveSupplierPaymentFromDashboard,
        })}
        {renderDashboardPaymentModal({
          open: stallCreditPaymentModal.open,
          viewOnly: stallCreditPaymentModal.viewOnly,
          title: stallCreditPaymentModal.viewOnly ? "View Sales Settlement" : "Add Sales Settlement",
          nameLabel: "Customer",
          nameValue: stallCreditPaymentModal.customerName,
          label: stallCreditPaymentModal.label,
          totalAmount: stallCreditPaymentModal.totalAmount,
          paidAmount: stallCreditPaymentModal.paidAmount,
          paidDateSummary: stallCreditPaymentModal.paidDateSummary,
          discountAmount: stallCreditPaymentModal.discountAmount,
          knockOffAmount: stallCreditPaymentModal.knockOffAmount,
          pendingAmount: stallCreditPaymentModal.pendingAmount,
          form: stallCreditPaymentForm,
          onChangeForm: setStallCreditPaymentForm,
          saving: stallCreditPaymentSaving,
          onClose: closeStallCreditPaymentModal,
          onSave: saveStallCreditPaymentFromDashboard,
        })}
        {renderDashboardPaymentModal({
          open: marketCreditPaymentModal.open,
          viewOnly: marketCreditPaymentModal.viewOnly,
          title: marketCreditPaymentModal.viewOnly ? "View Stall Billing Settlement" : "Add Stall Billing Settlement",
          nameLabel: "Customer",
          nameValue: marketCreditPaymentModal.customerName,
          label: marketCreditPaymentModal.label,
          totalAmount: marketCreditPaymentModal.totalAmount,
          paidAmount: marketCreditPaymentModal.paidAmount,
          paidDateSummary: marketCreditPaymentModal.paidDateSummary,
          discountAmount: marketCreditPaymentModal.discountAmount,
          knockOffAmount: marketCreditPaymentModal.knockOffAmount,
          pendingAmount: marketCreditPaymentModal.pendingAmount,
          form: marketCreditPaymentForm,
          onChangeForm: setMarketCreditPaymentForm,
          saving: marketCreditPaymentSaving,
          onClose: closeMarketCreditPaymentModal,
          onSave: saveMarketCreditPaymentFromDashboard,
        })}
      </div>
    </div>
  );
}
